import React from 'react';
import { validateStrategyNumbers } from './strategyValidation';
import moment from 'moment';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import DashboardMenu from '../../components/dashboardMenu/dashboardMenu';
import Grid from '../../components/grid/grid';
import useApi, { CONTROLLER } from '../../hooks/useApi';
import useAOS from '../../hooks/useAOS';
import SingleDatePicker from '../../components/ui/singleDatePicker';
import { StoreContext } from '../../store';
import './strategies.css';

const horizonsAll = [
  { value: 'daily', label: 'Daily' },
  { value: 'two_day', label: 'Two-Day' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'two_week', label: 'Two-Week' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'two_month', label: 'Two-Month' },
  { value: 'three_month', label: 'Three-Month' },
  { value: 'six_month', label: 'Six-Month' },
  { value: 'yearly', label: 'Yearly' },
];

const horizonsBasic = [
  { value: 'daily', label: 'Daily' },
  { value: 'two_day', label: 'Two-Day' },
  { value: 'weekly', label: 'Weekly' },
];

const actions = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
  { value: 'short', label: 'Short' },
  { value: 'hold', label: 'No Trade' },
];

const STRATEGY_MODE_PREDICTION = 'prediction';
const STRATEGY_MODE_CUSTOM_RULE = 'custom_rule';
const STRATEGY_MODE_HYBRID = 'hybrid';

const strategyModeOptions = [
  { value: STRATEGY_MODE_PREDICTION, label: 'DeepTrading Prediction' },
  { value: STRATEGY_MODE_CUSTOM_RULE, label: 'Custom Rules' },
  { value: STRATEGY_MODE_HYBRID, label: 'Hybrid' },
];

const defaultRuleDefinition = {
  version: 1,
  default_action: 'hold',
  rules: [
    {
      id: 'rule_1',
      label: 'RSI Below 30',
      action: 'buy',
      signal_score: 1,
      when: {
        all: [
          {
            field: 'indicator.rsi_14',
            operator: 'lt',
            value: 30,
          },
        ],
      },
    },
  ],
};

const fallbackRuleFields = [
  { id: 'daily.close', label: 'Close', category: 'Daily Historical', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'daily.volume', label: 'Volume', category: 'Daily Historical', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'indicator.sma_close_20', label: '20-Day SMA', category: 'Derived Indicators', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'indicator.sma_close_50', label: '50-Day SMA', category: 'Derived Indicators', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'indicator.rsi_14', label: '14-Day RSI', category: 'Derived Indicators', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'indicator.return_1d', label: '1-Day Historical Return', category: 'Derived Indicators', type: 'number', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'stock_return.daily_return_pct', label: '1D Actual Return (%)', category: 'Stock Return Data', type: 'number', value_unit: 'percent_points', input_hint: 'Enter percent points. Example: 3 means 3%.', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'stock_return.weekly_return_pct', label: '1W Actual Return (%)', category: 'Stock Return Data', type: 'number', value_unit: 'percent_points', input_hint: 'Enter percent points. Example: 3 means 3%.', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
  { id: 'market.sentiment_label', label: 'Market Sentiment', category: 'Market Analysis', type: 'text', allowed_modes: [STRATEGY_MODE_CUSTOM_RULE, STRATEGY_MODE_HYBRID] },
];

const fallbackRuleOperators = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
  { value: 'neq', label: '!=' },
  { value: 'between', label: 'Between' },
  { value: 'crosses_above', label: 'Crosses Above' },
  { value: 'crosses_below', label: 'Crosses Below' },
  { value: 'is_null', label: 'Is Missing' },
  { value: 'is_not_null', label: 'Is Present' },
];

const sentimentRuleValueOptions = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

const cloneRuleDefinition = (definition) => JSON.parse(JSON.stringify(definition || defaultRuleDefinition));

const normalizeRuleDefinitionForForm = (definition) => {
  const next = cloneRuleDefinition(definition);
  if (!Array.isArray(next.rules) || !next.rules.length) {
    next.rules = cloneRuleDefinition(defaultRuleDefinition).rules;
  }
  const rule = next.rules[0];
  if (!rule.when || typeof rule.when !== 'object') {
    rule.when = cloneRuleDefinition(defaultRuleDefinition).rules[0].when;
  }
  if (!Array.isArray(rule.when.all) || !rule.when.all.length) {
    rule.when.all = cloneRuleDefinition(defaultRuleDefinition).rules[0].when.all;
  }
  next.version = 1;
  next.default_action = next.default_action || 'hold';
  rule.id = rule.id || 'rule_1';
  rule.label = rule.label || 'Rule 1';
  rule.action = rule.action || 'buy';
  rule.signal_score = Number.isFinite(Number(rule.signal_score)) ? Number(rule.signal_score) : 1;
  return next;
};

const getPrimaryRule = (definition) => normalizeRuleDefinitionForForm(definition).rules[0];

const getPrimaryCondition = (definition) => getPrimaryRule(definition).when.all[0];

const operatorNeedsValue = (operator) => !['is_null', 'is_not_null'].includes(String(operator || ''));

const normalizeRuleValueForOperator = (operator, currentValue) => {
  if (!operatorNeedsValue(operator)) return undefined;
  if (operator === 'between') {
    return Array.isArray(currentValue) ? currentValue : ['', ''];
  }
  return Array.isArray(currentValue) ? currentValue[0] || '' : currentValue;
};

const sentimentConditions = [
  { value: 'any', label: 'Any' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

const missingSentimentBehaviors = [
  { value: 'treat_neutral', label: 'Treat Missing As Neutral' },
  { value: 'ignore', label: 'Ignore Sentiment Filter' },
  { value: 'no_trade', label: 'No Trade' },
];

const instrumentTypes = [
  { value: 'stock', label: 'Stocks' },
  { value: 'option', label: 'Options' },
];

const optionTypes = [
  { value: 'call', label: 'Calls' },
  { value: 'put', label: 'Puts' },
];

const optionActionModes = [
  { value: 'prediction_driven', label: 'Match Selected Contract To Signal' },
  { value: 'calls_only', label: 'BUY/SELL CALL Only' },
  { value: 'puts_only', label: 'BUY/SELL PUT Only' },
];

const optionPriceModels = [
  { value: 'bid_ask', label: 'Entry Ask / Exit Bid' },
  { value: 'midpoint', label: 'Midpoint' },
];

const OPTION_TEMPLATE_CUSTOM = 'custom';
const OPTION_TEMPLATE_BALANCED = 'balanced_directional';
const optionTemplatePresets = [
  {
    value: OPTION_TEMPLATE_CUSTOM,
    label: 'Build My Own',
    description: 'Choose your own options settings.',
    settings: {},
  },
  {
    value: 'conservative_directional',
    label: 'Conservative Directional',
    description: 'Prioritizes executed trades, fewer skips, and tighter spreads before return.',
    settings: {
      optionMaxSpreadPct: 25,
      optionMinVolume: '',
      optionMinOpenInterest: '',
      optionPriceModel: 'bid_ask',
      autoReselectOptionContract: true,
    },
  },
  {
    value: OPTION_TEMPLATE_BALANCED,
    label: 'Balanced Directional',
    description: 'Balances historical return, execution count, skipped trades, and spread discipline.',
    settings: {
      optionMaxSpreadPct: 35,
      optionMinVolume: '',
      optionMinOpenInterest: '',
      optionPriceModel: 'bid_ask',
      autoReselectOptionContract: true,
    },
  },
  {
    value: 'liquidity_first',
    label: 'Liquidity First',
    description: 'Prefers contracts with tighter spreads and at least some entry-day volume.',
    settings: {
      optionMaxSpreadPct: 20,
      optionMinVolume: 1,
      optionMinOpenInterest: '',
      optionPriceModel: 'bid_ask',
      autoReselectOptionContract: true,
    },
  },
  {
    value: 'capital_efficient',
    label: 'Capital Efficient',
    description: 'Prefers contracts that fit the selected capital and reduce insufficient-capital skips.',
    settings: {
      optionMaxSpreadPct: 50,
      optionMinVolume: '',
      optionMinOpenInterest: '',
      optionPriceModel: 'bid_ask',
      autoReselectOptionContract: true,
    },
  },
  {
    value: 'aggressive_directional',
    label: 'Aggressive Directional',
    description: 'Ranks primarily by historical options return after at least one executable trade.',
    settings: {
      optionMaxSpreadPct: '',
      optionMinVolume: '',
      optionMinOpenInterest: '',
      optionPriceModel: 'bid_ask',
      autoReselectOptionContract: true,
    },
  },
];

const optionTemplateById = optionTemplatePresets.reduce((acc, preset) => {
  acc[preset.value] = preset;
  return acc;
}, {});

const optionActionModeForType = (optionType) => (optionType === 'put' ? 'puts_only' : 'calls_only');

const optionTemplateControlledFields = new Set([
  'optionActionMode',
  'optionPriceModel',
  'optionMaxSpreadPct',
  'optionMinVolume',
  'optionMinOpenInterest',
  'autoReselectOptionContract',
]);

const normalizeTemplateComparableValue = (value) => {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text) return '';
  const number = Number(text);
  return Number.isFinite(number) ? String(number) : text;
};

const optionTemplateMatchesSettings = (preset, settings) => {
  if (!preset || preset.value === OPTION_TEMPLATE_CUSTOM) return false;
  const expected = preset.settings || {};
  return (
    String(settings.optionPriceModel || 'bid_ask') === String(expected.optionPriceModel || 'bid_ask') &&
    String(settings.optionActionMode || optionActionModeForType(settings.optionType)) === optionActionModeForType(settings.optionType) &&
    Boolean(settings.autoReselectOptionContract) === Boolean(expected.autoReselectOptionContract) &&
    normalizeTemplateComparableValue(settings.optionMaxSpreadPct) === normalizeTemplateComparableValue(expected.optionMaxSpreadPct) &&
    normalizeTemplateComparableValue(settings.optionMinVolume) === normalizeTemplateComparableValue(expected.optionMinVolume) &&
    normalizeTemplateComparableValue(settings.optionMinOpenInterest) === normalizeTemplateComparableValue(expected.optionMinOpenInterest)
  );
};

const inferOptionTemplateId = (settings) => {
  const match = optionTemplatePresets.find((preset) => optionTemplateMatchesSettings(preset, settings));
  return match?.value || OPTION_TEMPLATE_CUSTOM;
};

const optionTemplatePatch = (current, templateId) => {
  const preset = optionTemplateById[templateId] || optionTemplateById[OPTION_TEMPLATE_CUSTOM];
  if (!preset || preset.value === OPTION_TEMPLATE_CUSTOM) {
    return { optionTemplateId: OPTION_TEMPLATE_CUSTOM };
  }
  const optionType = current.optionType === 'put' ? 'put' : 'call';
  const settings = preset.settings || {};
  return {
    optionTemplateId: preset.value,
    optionActionMode: optionActionModeForType(optionType),
    optionPriceModel: settings.optionPriceModel || 'bid_ask',
    optionMaxSpreadPct: settings.optionMaxSpreadPct == null ? '' : settings.optionMaxSpreadPct,
    optionMinVolume: settings.optionMinVolume == null ? '' : settings.optionMinVolume,
    optionMinOpenInterest: settings.optionMinOpenInterest == null ? '' : settings.optionMinOpenInterest,
    autoReselectOptionContract: Boolean(settings.autoReselectOptionContract),
  };
};

const STOCK_TEMPLATE_CUSTOM = 'custom';
const stockTemplatePresets = [
  {
    value: STOCK_TEMPLATE_CUSTOM,
    label: 'Build My Own',
    description: 'Choose your own strategy settings.',
    settings: {},
  },
  {
    value: 'balanced_directional',
    label: 'Balanced Directional',
    description: 'Buys positive signals and exits/stays flat on negative signals on a weekly cadence.',
    settings: {
      tradeFrequencyBdays: 5,
      upAction: 'buy',
      downAction: 'sell',
      sentimentCondition: 'any',
      missingSentimentBehavior: 'treat_neutral',
      minPredictedReturn: '',
      maxPredictedReturn: '',
    },
  },
  {
    value: 'long_short_directional',
    label: 'Long / Short Directional',
    description: 'Buys positive signals and shorts negative signals on a weekly cadence.',
    settings: {
      tradeFrequencyBdays: 5,
      upAction: 'buy',
      downAction: 'short',
      sentimentCondition: 'any',
      missingSentimentBehavior: 'treat_neutral',
      minPredictedReturn: '',
      maxPredictedReturn: '',
    },
  },
  {
    value: 'long_only',
    label: 'Long Only',
    description: 'Buys positive signals and avoids negative signals.',
    settings: {
      tradeFrequencyBdays: 5,
      upAction: 'buy',
      downAction: 'hold',
      sentimentCondition: 'any',
      missingSentimentBehavior: 'treat_neutral',
      minPredictedReturn: '',
      maxPredictedReturn: '',
    },
  },
  {
    value: 'high_conviction',
    label: 'High Conviction',
    description: 'Trades only when the predicted return is at least +2%.',
    settings: {
      tradeFrequencyBdays: 5,
      upAction: 'buy',
      downAction: 'hold',
      sentimentCondition: 'any',
      missingSentimentBehavior: 'treat_neutral',
      minPredictedReturn: 0.02,
      maxPredictedReturn: '',
    },
  },
  {
    value: 'sentiment_confirmed',
    label: 'Sentiment Confirmed',
    description: 'Takes long trades only when Market Analysis sentiment is positive.',
    settings: {
      tradeFrequencyBdays: 5,
      upAction: 'buy',
      downAction: 'hold',
      sentimentCondition: 'positive',
      missingSentimentBehavior: 'no_trade',
      minPredictedReturn: '',
      maxPredictedReturn: '',
    },
  },
  {
    value: 'lower_turnover',
    label: 'Lower Turnover',
    description: 'Uses the same long-only signal with fewer decisions.',
    settings: {
      tradeFrequencyBdays: 10,
      upAction: 'buy',
      downAction: 'hold',
      sentimentCondition: 'any',
      missingSentimentBehavior: 'treat_neutral',
      minPredictedReturn: '',
      maxPredictedReturn: '',
    },
  },
];

const stockTemplateById = stockTemplatePresets.reduce((acc, preset) => {
  acc[preset.value] = preset;
  return acc;
}, {});

const stockTemplateControlledFields = new Set([
  'tradeFrequencyBdays',
  'upAction',
  'downAction',
  'sentimentCondition',
  'missingSentimentBehavior',
  'minPredictedReturn',
  'maxPredictedReturn',
]);

const stockTemplateMatchesSettings = (preset, settings) => {
  if (!preset || preset.value === STOCK_TEMPLATE_CUSTOM) return false;
  const expected = preset.settings || {};
  return (
    normalizeTemplateComparableValue(settings.tradeFrequencyBdays) === normalizeTemplateComparableValue(expected.tradeFrequencyBdays) &&
    String(settings.upAction || 'buy') === String(expected.upAction || 'buy') &&
    String(settings.downAction || 'hold') === String(expected.downAction || 'hold') &&
    String(settings.sentimentCondition || 'any') === String(expected.sentimentCondition || 'any') &&
    String(settings.missingSentimentBehavior || 'treat_neutral') === String(expected.missingSentimentBehavior || 'treat_neutral') &&
    normalizeTemplateComparableValue(settings.minPredictedReturn) === normalizeTemplateComparableValue(expected.minPredictedReturn) &&
    normalizeTemplateComparableValue(settings.maxPredictedReturn) === normalizeTemplateComparableValue(expected.maxPredictedReturn)
  );
};

const inferStockTemplateId = (settings) => {
  const match = stockTemplatePresets.find((preset) => stockTemplateMatchesSettings(preset, settings));
  return match?.value || STOCK_TEMPLATE_CUSTOM;
};

const stockTemplatePatch = (templateId) => {
  const preset = stockTemplateById[templateId] || stockTemplateById[STOCK_TEMPLATE_CUSTOM];
  if (!preset || preset.value === STOCK_TEMPLATE_CUSTOM) {
    return { stockTemplateId: STOCK_TEMPLATE_CUSTOM };
  }
  const settings = preset.settings || {};
  return {
    stockTemplateId: preset.value,
    tradeFrequencyBdays: settings.tradeFrequencyBdays == null ? 1 : settings.tradeFrequencyBdays,
    upAction: settings.upAction || 'buy',
    downAction: settings.downAction || 'hold',
    sentimentCondition: settings.sentimentCondition || 'any',
    missingSentimentBehavior: settings.missingSentimentBehavior || 'treat_neutral',
    minPredictedReturn: settings.minPredictedReturn == null ? '' : settings.minPredictedReturn,
    maxPredictedReturn: settings.maxPredictedReturn == null ? '' : settings.maxPredictedReturn,
  };
};

const clampNumber = (value, minValue, maxValue) => {
  if (value === '' || value == null) return value;
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  if (minValue != null && num < minValue) return minValue;
  if (maxValue != null && num > maxValue) return maxValue;
  return num;
};

const formatPct = (value) => {
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const formatPlainPct = (value) => {
  if (!Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}%`;
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDollars = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `$${formatMoney(number)}`;
};

const optionActionLabels = {
  buy_call: 'BUY CALL',
  sell_call: 'SELL CALL',
  buy_put: 'BUY PUT',
  sell_put: 'SELL PUT',
  hold: 'NO TRADE',
  no_trade: 'NO TRADE',
};

const formatOptionAction = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return optionActionLabels[key] || 'NO TRADE';
};

const formatSkipReason = (value) => {
  const key = String(value || '').trim();
  if (!key) return '—';
  return key.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
};

const formatContractSummary = (contract) => {
  if (!contract) return '';
  const symbol = String(contract.option_symbol || '').trim();
  const type = String(contract.contract_type || '').trim().toUpperCase();
  const strike = Number(contract.strike);
  const strikeText = Number.isFinite(strike) ? `$${strike.toFixed(2)}` : '';
  const expiration = contract.expiration_date ? `Exp ${contract.expiration_date}` : '';
  return [symbol, type, strikeText, expiration].filter(Boolean).join(' | ');
};

const isValidDateValue = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const getStrategyActivationIssues = (form, optionsRiskAcknowledged) => {
  const issues = [];
  if (!String(form.ticker || '').trim()) issues.push('ticker');
  if (!String(form.horizon || '').trim()) issues.push('prediction horizon');
  if (!isValidDateValue(form.fromDate) || !isValidDateValue(form.toDate)) {
    issues.push('valid date range');
  } else if (new Date(form.fromDate) > new Date(form.toDate)) {
    issues.push('valid date range');
  }
  if (form.instrumentType === 'option') {
    if (!optionsRiskAcknowledged) issues.push('options risk acknowledgement');
    if (!String(form.optionSymbol || '').trim()) {
      issues.push('historical option contract');
    }
  }
  if (form.instrumentType === 'stock' && form.strategyMode !== STRATEGY_MODE_PREDICTION) {
    const condition = getPrimaryCondition(form.ruleDefinition);
    if (!String(condition?.field || '').trim() || !String(condition?.operator || '').trim()) {
      issues.push('custom rule');
    } else if (operatorNeedsValue(condition.operator)) {
      const value = condition.value;
      if (condition.operator === 'between') {
        if (!Array.isArray(value) || value.some((entry) => String(entry ?? '').trim() === '')) {
          issues.push('custom rule value');
        }
      } else if (String(value ?? '').trim() === '') {
        issues.push('custom rule value');
      }
    }
  }
  return issues;
};

const formatActivationIssues = (issues) => {
  if (!issues.length) return '';
  if (issues.length === 1) return issues[0];
  if (issues.length === 2) return `${issues[0]} and ${issues[1]}`;
  return `${issues.slice(0, -1).join(', ')}, and ${issues[issues.length - 1]}`;
};

const formatPctPoints = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const sign = number > 0 ? '+' : '';
  return `${sign}${number.toFixed(2)} pts`;
};

const optionActionTone = (value) => {
  const label = formatOptionAction(value);
  if (label.startsWith('BUY')) return 'buy';
  if (label.startsWith('SELL')) return 'sell';
  return 'hold';
};

const formatRecommendationAction = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (optionActionLabels[key]) return formatOptionAction(key);
  if (key === 'buy') return 'BUY';
  if (key === 'sell') return 'SELL';
  if (key === 'short') return 'SHORT';
  return 'NO TRADE';
};

const recommendationActionTone = (value) => {
  const key = String(value || '').trim().toLowerCase();
  if (key.startsWith('buy')) return 'buy';
  if (key.startsWith('sell') || key.startsWith('short')) return 'sell';
  return 'hold';
};

const OptionActionPill = ({ value }) => (
  <span className={`dt-option-action-pill dt-option-action-pill--${optionActionTone(value)}`}>
    {formatOptionAction(value)}
  </span>
);

const escapeCsvValue = (value) => {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildCsv = ({ columns, rows }) => {
  const headerRow = columns.map((c) => escapeCsvValue(c.header || c.name)).join(',');
  const dataRows = rows.map((row) => columns.map((c) => escapeCsvValue(row[c.name])).join(','));
  return [headerRow, ...dataRows].join('\n');
};

const downloadTextFile = ({ contents, filename, mimeType }) => {
  const blob = new Blob([contents], { type: mimeType || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const FIELD_HELP = {
  active: 'If Active, we show today’s recommended action for this strategy.',
  emailSuggestedAction: 'Send today’s suggested trading action to the email from your profile settings.',
  marketplacePublic: 'When enabled, this strategy appears in the public Strategy Marketplace and leaderboard.',
  marketplaceShowParameters: 'If enabled, users can view this strategy’s settings in the marketplace detail page.',
  marketplaceAllowClone: 'If enabled, users can clone this strategy to their account (requires Show Parameters).',
  savedStrategy: 'Select a previously saved strategy to load its settings, or start a new one.',
  strategyName: 'A name to save and reuse this strategy later.',
  ticker: 'The stock you want to backtest.',
  horizon: 'Which return window to act on (Daily, Weekly, Monthly, etc.). Prediction mode uses the matching DeepTrading prediction horizon.',
  tradeFrequency: 'How often you allow a decision, in business days (1=daily, 5≈weekly, 21≈monthly).',
  fromDate: 'Start date for the backtest window (business days, not calendar days).',
  toDate: 'End date for the backtest window (business days, not calendar days).',
  initialCapital: 'Starting portfolio value for the simulated equity curve.',
  upAction: 'What to do when the predicted return is ≥ 0 (Buy/Sell/Short/No Trade). Sell exits or stays flat; Short profits if the stock falls.',
  downAction: 'What to do when the predicted return is < 0 (Buy/Sell/Short/No Trade). Sell exits or stays flat; Short profits if the stock falls.',
  sentimentCondition: 'Optional: require Market Analysis sentiment (Positive/Neutral/Negative) before taking any trade action.',
  missingSentimentBehavior: 'If sentiment is missing for a date, choose how to treat it. Default: Treat Missing As Neutral.',
  minPredictedReturn: 'Optional: only trade when predicted return is ≥ this value (e.g., 0.02 = +2%). Leave blank for no minimum.',
  maxPredictedReturn: 'Optional: only trade when predicted return is ≤ this value (e.g., -0.10 = -10%). Leave blank for no maximum.',
  instrumentType: 'Stocks runs the current strategy backtest. Options simulates BUY/SELL CALL or BUY/SELL PUT actions from DeepTrading stock signals.',
  strategyMode: 'Choose whether strategy actions come from DeepTrading predictions, custom rules, or custom rules that can reference prediction fields.',
  ruleField: 'Database or derived field evaluated by the custom rule.',
  ruleOperator: 'Comparison operator for the selected field.',
  ruleValue: 'Comparison value used by the selected operator.',
  ruleAction: 'Trading action used when the custom rule matches.',
  ruleDefaultAction: 'Trading action used when no custom rule matches.',
  stockTemplate: 'Choose a standard stock strategy preset. Selecting one updates the strategy parameters; the template comparison is calculated after the backtest.',
  optionTemplate: 'Choose a predefined options parameter set. Returns are calculated only after running the backtest for your ticker, dates, capital, and signal.',
  optionType: 'Choose whether this options backtest evaluates call or put contracts.',
  optionActionMode: 'Choose the options action universe. BUY opens a long option; SELL closes that long option. Option writing is not modeled.',
  optionPriceModel: 'Entry Ask / Exit Bid is conservative. Midpoint is less conservative and can overstate fills.',
  optionContract: 'Only contracts with available historical option rows are listed.',
  optionAutoReselectContract: 'If enabled, Generate Backtest may choose a different affordable contract for each trade date. If disabled, the selected historical contract is used for all option trades.',
  optionMaxSpreadPct: 'Optional: skip option trades when the entry bid/ask spread exceeds this percent.',
  optionMinVolume: 'Optional: skip option trades when the entry option volume is below this number.',
  optionMinOpenInterest: 'Optional: skip option trades when entry open interest is below this number.',
  backtestTrades: 'Number of Buy or Short rows with return exposure. Sell and No Trade rows are flat.',
  backtestWinRate: 'Percent of executed trades that finished with a positive return.',
  backtestTotalReturn: 'Total compounded return of the strategy over the selected window.',
  backtestBuyHoldReturn: 'Close-to-close return of buying on the first day and holding until the last day in the selected window.',
  backtestSkippedOptionSignals: 'Option signal rows that did not execute because prices were missing, filters rejected the row, the selected contract side did not match the signal, or capital was insufficient.',
  marketBenchmark: 'Buy-and-hold return for the market benchmark over the same backtest window, using historical close prices.',
  backtestFinalEquity: 'Ending portfolio value after applying each trade return to your Initial Capital.',
};

const STRATEGIES_TABLE_MIN_HEIGHT = 380;
const STRATEGIES_TABLE_MAX_HEIGHT = 1400;
const STRATEGIES_TABLE_DEFAULT_HEIGHT = 560;
const STRATEGIES_PANEL_MIN_WIDTH_PERCENT = 88;
const STRATEGIES_PANEL_DEFAULT_WIDTH_PERCENT = 100;
const STRATEGIES_RESIZE_MODE_Y = 'y';
const STRATEGIES_RESIZE_MODE_X = 'x';
const STRATEGIES_RESIZE_MODE_BOTH = 'both';
const STRATEGIES_RESIZE_CLASS_Y = 'dt-strategies-resizing-y';
const STRATEGIES_RESIZE_CLASS_X = 'dt-strategies-resizing-x';
const STRATEGIES_RESIZE_CLASS_BOTH = 'dt-strategies-resizing-both';
const STRATEGIES_BACKTEST_TABLE_PREFERENCE = 'strategiesBacktest_v2';
const STRATEGIES_OPTIONS_BACKTEST_TABLE_PREFERENCE = 'strategiesOptionsBacktest_v1';

const clampStrategiesTableHeight = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return STRATEGIES_TABLE_DEFAULT_HEIGHT;
  }
  return Math.max(STRATEGIES_TABLE_MIN_HEIGHT, Math.min(STRATEGIES_TABLE_MAX_HEIGHT, Math.round(num)));
};

const clampStrategiesPanelWidthPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return STRATEGIES_PANEL_DEFAULT_WIDTH_PERCENT;
  }
  return Math.max(STRATEGIES_PANEL_MIN_WIDTH_PERCENT, num);
};

const clearStrategiesResizeBodyClasses = () => {
  if (typeof document === 'undefined') return;
  document.body.classList.remove(STRATEGIES_RESIZE_CLASS_Y, STRATEGIES_RESIZE_CLASS_X, STRATEGIES_RESIZE_CLASS_BOTH);
};

const setStrategiesResizeBodyClass = (mode) => {
  if (typeof document === 'undefined') return;
  clearStrategiesResizeBodyClasses();
  if (mode === STRATEGIES_RESIZE_MODE_X) {
    document.body.classList.add(STRATEGIES_RESIZE_CLASS_X);
    return;
  }
  if (mode === STRATEGIES_RESIZE_MODE_BOTH) {
    document.body.classList.add(STRATEGIES_RESIZE_CLASS_BOTH);
    return;
  }
  document.body.classList.add(STRATEGIES_RESIZE_CLASS_Y);
};

const HelpTip = ({ id, text, className }) => (
  <OverlayTrigger
    placement="top"
    overlay={(
      <Tooltip id={id} className="dt-tooltip">
        {text}
      </Tooltip>
    )}
  >
    <span
      className={`dt-help-icon ${className || ''}`}
      role="button"
      tabIndex={0}
      aria-label={text}
      onMouseDown={(e) => e.preventDefault()}
    >
      ?
    </span>
  </OverlayTrigger>
);

const STRATEGY_HELP_TITLE = 'Trading Strategies: Help';
const TRADES_HELP_TITLE = 'Trades Table: Column Guide';

const HelpModalIcon = ({ onClick, ariaLabel }) => (
  <button
    type="button"
    className="dt-help-modal-icon"
    aria-label={ariaLabel}
    onClick={onClick}
  >
    ?
  </button>
);

const Strategies = () => {
  const { push } = useHistory();
  const { store: { paid, advanced } } = React.useContext(StoreContext);
  const strategyMarketplaceEnabled = process.env.REACT_APP_ENABLE_STRATEGY_MARKETPLACE !== '0';
  const optionsStrategiesEnabled = process.env.REACT_APP_ENABLE_OPTIONS_STRATEGIES === '1' && (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_OPTIONS_STRATEGIES_PROD === '1');
  const customStrategiesEnabled = process.env.REACT_APP_ENABLE_CUSTOM_STRATEGIES === '1' && (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_CUSTOM_STRATEGIES_PROD === '1');

  useAOS();

  const { payload: tickersPayload, execute: loadTickers } = useApi(CONTROLLER.TICKERS_LOAD);
  const { payload: strategiesPayload, loading: strategiesLoading, execute: loadStrategies } = useApi(CONTROLLER.STRATEGIES_LIST);
  const { payload: strategyFieldsPayload, execute: loadStrategyFields } = useApi(CONTROLLER.STRATEGIES_FIELDS);
  const { loading: saveLoading, execute: saveStrategyApi } = useApi(CONTROLLER.STRATEGIES_SAVE);
  const { loading: backtestLoading, execute: runBacktest } = useApi(CONTROLLER.STRATEGIES_BACKTEST);
  const { payload: optionContractsPayload, loading: optionContractsLoading, execute: loadOptionContractsApi } = useApi(CONTROLLER.STRATEGIES_OPTIONS_CONTRACTS);
  const { loading: optionsBacktestLoading, execute: runOptionsBacktest } = useApi(CONTROLLER.STRATEGIES_OPTIONS_BACKTEST);
  const { loading: optionsSelectContractLoading, execute: selectOptionsContractApi } = useApi(CONTROLLER.STRATEGIES_OPTIONS_SELECT_CONTRACT);
  const {
    payload: optionsRiskPayload,
    loading: optionsRiskLoading,
    execute: loadOptionsRiskAcknowledgement,
  } = useApi(CONTROLLER.STRATEGIES_OPTIONS_RISK_ACKNOWLEDGEMENT);
  const {
    loading: optionsRiskAcceptLoading,
    execute: acceptOptionsRiskAcknowledgement,
  } = useApi(CONTROLLER.STRATEGIES_OPTIONS_ACCEPT_RISK_ACKNOWLEDGEMENT);
  const { payload: marketplaceMinePayload, execute: loadMarketplaceMine } = useApi(CONTROLLER.STRATEGIES_MARKETPLACE_MINE);
  const { loading: marketplacePublishLoading, execute: publishMarketplaceApi } = useApi(CONTROLLER.STRATEGIES_MARKETPLACE_PUBLISH);
  const { loading: marketplaceUnpublishLoading, execute: unpublishMarketplaceApi } = useApi(CONTROLLER.STRATEGIES_MARKETPLACE_UNPUBLISH);

  const savedStrategies = React.useMemo(() => strategiesPayload?.data?.strategies || [], [strategiesPayload]);
  const visibleSavedStrategies = React.useMemo(
    () => savedStrategies.filter((s) => optionsStrategiesEnabled || s.instrument_type !== 'option'),
    [optionsStrategiesEnabled, savedStrategies],
  );
  const activeRecommendation = strategiesPayload?.data?.active_recommendation;
  const strategyFields = React.useMemo(() => (
    strategyFieldsPayload?.data?.fields || fallbackRuleFields
  ), [strategyFieldsPayload]);
  const strategyOperators = React.useMemo(() => (
    strategyFieldsPayload?.data?.operators || fallbackRuleOperators
  ), [strategyFieldsPayload]);
  const strategyActions = React.useMemo(() => (
    strategyFieldsPayload?.data?.actions || actions
  ), [strategyFieldsPayload]);
  const customStrategyPersistenceEnabled = React.useMemo(() => (
    strategyFieldsPayload?.data?.persistence_enabled !== false
  ), [strategyFieldsPayload]);
  const marketplaceMineItems = React.useMemo(() => marketplaceMinePayload?.data?.items || [], [marketplaceMinePayload]);
  const marketplaceByStrategyId = React.useMemo(() => {
    const out = {};
    for (const item of marketplaceMineItems) {
      const strategyId = Number(item?.strategy_id || item?.strategyId);
      if (!Number.isFinite(strategyId) || strategyId <= 0) continue;
      out[strategyId] = item;
    }
    return out;
  }, [marketplaceMineItems]);

  const defaultFromDate = React.useMemo(
    () => moment().subtract(2, 'months').format('YYYY-MM-DD'),
    [],
  );
  const defaultToDate = React.useMemo(
    () => moment().format('YYYY-MM-DD'),
    [],
  );

  const [form, setForm] = React.useState({
    savedStrategyName: '',
    strategyName: '',
    active: false,
    emailSuggestedAction: false,
    makePublic: false,
    showParametersPublicly: false,
    allowClone: false,
    ticker: '',
    horizon: 'daily',
    fromDate: defaultFromDate,
    toDate: defaultToDate,
    tradeFrequencyBdays: 1,
    upAction: 'buy',
    downAction: 'hold',
    sentimentCondition: 'any',
    missingSentimentBehavior: 'treat_neutral',
    minPredictedReturn: '',
    maxPredictedReturn: '',
    initialCapital: 10000,
    instrumentType: 'stock',
    strategyMode: STRATEGY_MODE_PREDICTION,
    ruleDefinition: cloneRuleDefinition(defaultRuleDefinition),
    stockTemplateId: STOCK_TEMPLATE_CUSTOM,
    optionType: 'call',
    optionTemplateId: OPTION_TEMPLATE_BALANCED,
    optionActionMode: 'calls_only',
    optionPriceModel: 'bid_ask',
    optionSymbol: '',
    autoReselectOptionContract: false,
    optionMaxSpreadPct: 35,
    optionMinVolume: '',
    optionMinOpenInterest: '',
  });

  const [dirty, setDirty] = React.useState(false);
  const [result, setResult] = React.useState();
  const [showHelp, setShowHelp] = React.useState(false);
  const [showTradesHelp, setShowTradesHelp] = React.useState(false);
  const [showOptionsRiskModal, setShowOptionsRiskModal] = React.useState(false);
  const [optionsRiskChecked, setOptionsRiskChecked] = React.useState(false);
  const [optionContractsMessage, setOptionContractsMessage] = React.useState('');
  const [optionContractsQueryKey, setOptionContractsQueryKey] = React.useState('');
  const [optionAutoSelectionSignature, setOptionAutoSelectionSignature] = React.useState('');
  const [selectedOptionContractDetails, setSelectedOptionContractDetails] = React.useState(null);
  const [strategyNotice, setStrategyNotice] = React.useState('');
  const [backtestError, setBacktestError] = React.useState('');
  const effectiveStrategyMode = customStrategiesEnabled && form.instrumentType === 'stock'
    ? form.strategyMode : STRATEGY_MODE_PREDICTION;
  const numericValidation = React.useMemo(
    () => validateStrategyNumbers(form, effectiveStrategyMode !== STRATEGY_MODE_CUSTOM_RULE),
    [form, effectiveStrategyMode],
  );
  const [tablePanelHeight, setTablePanelHeight] = React.useState(STRATEGIES_TABLE_DEFAULT_HEIGHT);
  const [resultPanelsWidthPercent, setResultPanelsWidthPercent] = React.useState(STRATEGIES_PANEL_DEFAULT_WIDTH_PERCENT);
  const resultsStackRef = React.useRef(null);
  const tradesCardRef = React.useRef(null);
  const tableResizeStateRef = React.useRef({
    active: false,
    mode: STRATEGIES_RESIZE_MODE_Y,
    startX: 0,
    startY: 0,
    startContainerWidth: 0,
    startHeight: STRATEGIES_TABLE_DEFAULT_HEIGHT,
    startWidthPercent: STRATEGIES_PANEL_DEFAULT_WIDTH_PERCENT,
  });

  const currentOptionContractsQueryKey = React.useMemo(() => (
    [
      form.ticker,
      form.optionType,
      form.fromDate,
      form.toDate,
      form.instrumentType,
    ].map((value) => String(value || '').trim().toUpperCase()).join('|')
  ), [form.fromDate, form.instrumentType, form.optionType, form.ticker, form.toDate]);

  const currentOptionAutoSelectionSignature = React.useMemo(() => (
    JSON.stringify({
      ticker: form.ticker,
      horizon: form.horizon,
      fromDate: form.fromDate,
      toDate: form.toDate,
      tradeFrequencyBdays: Number(form.tradeFrequencyBdays) || 1,
      upAction: form.upAction,
      downAction: form.downAction,
      sentimentCondition: form.sentimentCondition,
      missingSentimentBehavior: form.missingSentimentBehavior,
      minPredictedReturn: String(form.minPredictedReturn ?? '').trim(),
      maxPredictedReturn: String(form.maxPredictedReturn ?? '').trim(),
      initialCapital: String(form.initialCapital ?? '').trim(),
      optionType: form.optionType,
      optionActionMode: form.optionActionMode,
      optionPriceModel: form.optionPriceModel,
      optionMaxSpreadPct: String(form.optionMaxSpreadPct || '').trim(),
      optionMinVolume: String(form.optionMinVolume || '').trim(),
      optionMinOpenInterest: String(form.optionMinOpenInterest || '').trim(),
    })
  ), [form.downAction, form.fromDate, form.horizon, form.initialCapital, form.maxPredictedReturn, form.minPredictedReturn, form.missingSentimentBehavior, form.optionActionMode, form.optionMaxSpreadPct, form.optionMinOpenInterest, form.optionMinVolume, form.optionPriceModel, form.optionType, form.sentimentCondition, form.ticker, form.toDate, form.tradeFrequencyBdays, form.upAction]);

  const optionAutoSelectionStale = Boolean(
    form.instrumentType === 'option'
    && form.optionSymbol
    && optionAutoSelectionSignature
    && optionAutoSelectionSignature !== currentOptionAutoSelectionSignature,
  );

  const loadedOptionContracts = React.useMemo(
    () => optionContractsPayload?.data?.contracts || [],
    [optionContractsPayload],
  );
  const optionContractsLoadedForCurrentQuery = Boolean(
    optionContractsQueryKey && optionContractsQueryKey === currentOptionContractsQueryKey,
  );
  const canAutoSelectOptionContract = Boolean(
    form.ticker
    && form.fromDate
    && form.toDate
    && optionContractsLoadedForCurrentQuery
    && loadedOptionContracts.length > 0
    && !optionContractsLoading
    && !numericValidation.error
    && !optionsSelectContractLoading,
  );
  const autoSelectOptionContractTitle = React.useMemo(() => {
    if (numericValidation.error) return numericValidation.error;
    if (!form.ticker || !form.fromDate || !form.toDate) return 'Choose a ticker and date range first.';
    if (optionContractsLoading) return 'Contracts are loading.';
    if (!optionContractsLoadedForCurrentQuery) return 'Load contracts first.';
    if (!loadedOptionContracts.length) return 'No loaded contracts are available for auto-selection.';
    return 'Auto-select from the loaded contract list.';
  }, [numericValidation.error, form.fromDate, form.ticker, form.toDate, loadedOptionContracts.length, optionContractsLoadedForCurrentQuery, optionContractsLoading]);

  const selectedOptionContractFromResult = result?.option_contract_selection?.selected_contract;
  const selectedOptionContractForList = React.useMemo(() => {
    const selectedSymbol = String(form.optionSymbol || '').trim();
    if (!selectedSymbol) return null;
    const candidate = selectedOptionContractDetails || selectedOptionContractFromResult;
    const candidateSymbol = String(candidate?.option_symbol || '').trim();
    if (!candidateSymbol || candidateSymbol !== selectedSymbol) return null;
    return candidate;
  }, [form.optionSymbol, selectedOptionContractDetails, selectedOptionContractFromResult]);

  const optionContracts = React.useMemo(
    () => {
      const contracts = (
        optionContractsLoadedForCurrentQuery
          ? loadedOptionContracts
          : []
      );
      const selectedSymbol = String(form.optionSymbol || selectedOptionContractForList?.option_symbol || '').trim();
      if (!selectedSymbol) return contracts;
      if (contracts.some((contract) => String(contract.option_symbol || '').trim() === selectedSymbol)) return contracts;
      if (!selectedOptionContractForList?.option_symbol) return contracts;
      const selectedUnderlying = String(selectedOptionContractForList?.underlying_ticker || form.ticker || '').trim().toUpperCase();
      const currentTicker = String(form.ticker || '').trim().toUpperCase();
      if (selectedUnderlying && currentTicker && selectedUnderlying !== currentTicker) return contracts;
      return [selectedOptionContractForList, ...contracts];
    },
    [form.optionSymbol, form.ticker, loadedOptionContracts, optionContractsLoadedForCurrentQuery, selectedOptionContractForList],
  );

  const optionsRiskAcknowledgement = optionsRiskPayload?.data;
  const optionsRiskAcknowledged = Boolean(optionsRiskAcknowledgement?.acknowledged);
  const optionsRiskDisclaimer = optionsRiskAcknowledgement?.disclaimer || [
    'Options trading involves significant risk and is not suitable for all investors.',
    'Options can lose 100% of their premium and may expire worthless.',
    'DeepTrading.ai options strategies are educational simulations based on DeepTrading.ai stock predictions and historical option data.',
    'DeepTrading.ai does not place trades, does not provide financial advice, and does not guarantee outcomes.',
    'This release models long-options actions only: BUY CALL, SELL CALL, BUY PUT, SELL PUT, and NO TRADE.',
  ].join(' ');
  const optionsRiskExpiresDays = Number(optionsRiskAcknowledgement?.expires_in_days || 90);

  const currentOptionContractsQueryKeyRef = React.useRef(currentOptionContractsQueryKey);

  React.useEffect(() => {
    const previousQueryKey = currentOptionContractsQueryKeyRef.current;
    currentOptionContractsQueryKeyRef.current = currentOptionContractsQueryKey;
    if (previousQueryKey === currentOptionContractsQueryKey) return;
    setOptionContractsQueryKey('');
    setOptionContractsMessage('');
  }, [currentOptionContractsQueryKey]);

  React.useEffect(() => {
    if (paid === false) {
      push('/pricing-plans');
    }
  }, [paid, push]);

  React.useEffect(() => {
    if (!tickersPayload) {
      loadTickers();
    }
  }, [tickersPayload, loadTickers]);

  React.useEffect(() => {
    if (!strategiesPayload && paid) {
      loadStrategies();
    }
  }, [loadStrategies, paid, strategiesPayload]);

  React.useEffect(() => {
    if (paid && strategyMarketplaceEnabled) {
      loadMarketplaceMine();
    }
  }, [loadMarketplaceMine, paid, strategyMarketplaceEnabled]);

  React.useEffect(() => {
    if (paid && customStrategiesEnabled && !strategyFieldsPayload) {
      loadStrategyFields();
    }
  }, [customStrategiesEnabled, loadStrategyFields, paid, strategyFieldsPayload]);

  React.useEffect(() => {
    if (paid && optionsStrategiesEnabled && !optionsRiskPayload) {
      loadOptionsRiskAcknowledgement();
    }
  }, [loadOptionsRiskAcknowledgement, optionsRiskPayload, optionsStrategiesEnabled, paid]);

  React.useEffect(() => {
    if (!optionsStrategiesEnabled) {
      if (form.instrumentType === 'option') {
        setForm((prev) => ({
          ...prev,
          instrumentType: 'stock',
          optionSymbol: '',
          autoReselectOptionContract: false,
        }));
        setOptionAutoSelectionSignature('');
        setSelectedOptionContractDetails(null);
        setBacktestError('');
      }
      return;
    }
    if (form.instrumentType !== 'option') return;
    if (!optionsRiskAcknowledged) {
      setShowOptionsRiskModal(true);
    }
  }, [form.instrumentType, optionsRiskAcknowledged, optionsRiskPayload, optionsStrategiesEnabled]);

  React.useEffect(() => {
    if (customStrategiesEnabled) return;
    if (form.strategyMode === STRATEGY_MODE_PREDICTION) return;
    setForm((prev) => ({
      ...prev,
      strategyMode: STRATEGY_MODE_PREDICTION,
      ruleDefinition: cloneRuleDefinition(defaultRuleDefinition),
    }));
  }, [customStrategiesEnabled, form.strategyMode]);

  const horizons = React.useMemo(() => (advanced ? horizonsAll : horizonsBasic), [advanced]);
  const availableOptionActionModes = React.useMemo(() => (
    optionActionModes.filter((opt) => (
      opt.value === 'prediction_driven' ||
      (form.optionType === 'call' && opt.value === 'calls_only') ||
      (form.optionType === 'put' && opt.value === 'puts_only')
    ))
  ), [form.optionType]);
  const selectedStockTemplatePreset = stockTemplateById[form.stockTemplateId] || stockTemplateById[STOCK_TEMPLATE_CUSTOM];
  const selectedOptionTemplatePreset = optionTemplateById[form.optionTemplateId] || optionTemplateById[OPTION_TEMPLATE_CUSTOM];
  const selectedRuleDefinition = React.useMemo(
    () => normalizeRuleDefinitionForForm(form.ruleDefinition),
    [form.ruleDefinition],
  );
  const selectedRule = React.useMemo(() => getPrimaryRule(selectedRuleDefinition), [selectedRuleDefinition]);
  const selectedRuleCondition = React.useMemo(() => getPrimaryCondition(selectedRuleDefinition), [selectedRuleDefinition]);
  const ruleFieldsForMode = React.useMemo(() => (
    strategyFields.filter((field) => (field.allowed_modes || []).includes(form.strategyMode))
  ), [form.strategyMode, strategyFields]);
  const selectedRuleField = React.useMemo(() => (
    strategyFields.find((field) => field.id === selectedRuleCondition.field) || ruleFieldsForMode[0] || fallbackRuleFields[0]
  ), [ruleFieldsForMode, selectedRuleCondition.field, strategyFields]);
  const ruleOperatorsForField = React.useMemo(() => {
    const type = String(selectedRuleField?.type || 'number');
    const allowed = type === 'text'
      ? new Set(['eq', 'neq', 'is_null', 'is_not_null'])
      : type === 'date'
        ? new Set(['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'is_null', 'is_not_null'])
        : new Set(strategyOperators.map((operator) => operator.value));
    return strategyOperators.filter((operator) => allowed.has(operator.value));
  }, [selectedRuleField?.type, strategyOperators]);
  const ruleValueIsHidden = !operatorNeedsValue(selectedRuleCondition.operator);
  const selectedRuleFieldIsPercent = selectedRuleField?.value_unit === 'percent_points';
  const ruleValuePlaceholder = selectedRuleFieldIsPercent ? 'e.g., 3 for 3%' : 'Value';
  const ruleNumberStep = selectedRuleFieldIsPercent ? 0.01 : 0.001;

  React.useEffect(() => {
    if (advanced) return;
    if (!horizonsBasic.find((h) => h.value === form.horizon)) {
      setForm((prev) => ({ ...prev, horizon: 'daily' }));
      setDirty(true);
    }
  }, [advanced, form.horizon]);

  const activeStrategyName = React.useMemo(() => {
    const active = visibleSavedStrategies.find((s) => s.active);
    return active?.name || '';
  }, [visibleSavedStrategies]);

  const didAutoSelectActiveRef = React.useRef(false);

  React.useEffect(() => {
    if (didAutoSelectActiveRef.current) return;
    if (!visibleSavedStrategies.length) return;
    if (form.savedStrategyName) {
      didAutoSelectActiveRef.current = true;
      return;
    }
    if (activeStrategyName) {
      setForm((prev) => ({
        ...prev,
        savedStrategyName: activeStrategyName,
      }));
    }
    didAutoSelectActiveRef.current = true;
  }, [activeStrategyName, form.savedStrategyName, visibleSavedStrategies.length]);

  const updateField = React.useCallback((name, value) => {
    setForm((prev) => {
      if (name === 'stockTemplateId') {
        return {
          ...prev,
          ...stockTemplatePatch(value),
        };
      }

      if (name === 'optionTemplateId') {
        return {
          ...prev,
          ...optionTemplatePatch(prev, value),
        };
      }

      const next = { ...prev, [name]: value };

      if (name === 'strategyMode') {
        next.instrumentType = 'stock';
        next.stockTemplateId = STOCK_TEMPLATE_CUSTOM;
        if (value !== STRATEGY_MODE_PREDICTION) {
          next.ruleDefinition = normalizeRuleDefinitionForForm(prev.ruleDefinition);
        }
      }

      if (name === 'fromDate' && value && prev.toDate) {
        const from = new Date(value);
        const to = new Date(prev.toDate);
        if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && from > to) {
          next.toDate = value;
        }
      }

      if (name === 'toDate' && value && prev.fromDate) {
        const to = new Date(value);
        const from = new Date(prev.fromDate);
        if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && to < from) {
          next.fromDate = value;
        }
      }

      if (name === 'allowClone' && value) {
        next.showParametersPublicly = true;
      }

      if (name === 'showParametersPublicly' && !value) {
        next.allowClone = false;
      }

      if (name === 'optionType' && value === 'call' && prev.optionActionMode === 'puts_only') {
        next.optionActionMode = 'calls_only';
      }

      if (name === 'optionType' && value === 'put' && prev.optionActionMode === 'calls_only') {
        next.optionActionMode = 'puts_only';
      }

      if (name === 'optionType' && prev.optionTemplateId && prev.optionTemplateId !== OPTION_TEMPLATE_CUSTOM) {
        next.optionActionMode = optionActionModeForType(value);
      }

      if (optionTemplateControlledFields.has(name) && prev.optionTemplateId !== OPTION_TEMPLATE_CUSTOM) {
        next.optionTemplateId = OPTION_TEMPLATE_CUSTOM;
      }

      if (stockTemplateControlledFields.has(name) && prev.stockTemplateId !== STOCK_TEMPLATE_CUSTOM) {
        next.stockTemplateId = STOCK_TEMPLATE_CUSTOM;
      }

      if (name === 'optionSymbol') {
        next.autoReselectOptionContract = false;
        next.optionTemplateId = OPTION_TEMPLATE_CUSTOM;
      }

      return next;
    });
    if (name === 'optionSymbol') {
      setOptionAutoSelectionSignature('');
      setSelectedOptionContractDetails(null);
    }
    if (name !== 'active') {
      setStrategyNotice('');
      setBacktestError('');
    }
    setDirty(true);
  }, []);

  const updateRuleDefinition = React.useCallback((recipe) => {
    setForm((prev) => {
      const nextRuleDefinition = normalizeRuleDefinitionForForm(prev.ruleDefinition);
      recipe(nextRuleDefinition);
      return {
        ...prev,
        ruleDefinition: nextRuleDefinition,
        stockTemplateId: STOCK_TEMPLATE_CUSTOM,
      };
    });
    setStrategyNotice('');
    setBacktestError('');
    setDirty(true);
  }, []);

  const updatePrimaryRule = React.useCallback((patch) => {
    updateRuleDefinition((definition) => {
      const rule = definition.rules[0];
      Object.assign(rule, patch);
    });
  }, [updateRuleDefinition]);

  const updatePrimaryCondition = React.useCallback((patch) => {
    updateRuleDefinition((definition) => {
      const condition = definition.rules[0].when.all[0];
      Object.assign(condition, patch);
    });
  }, [updateRuleDefinition]);

  const updateRuleOperator = React.useCallback((operator) => {
    updatePrimaryCondition({
      operator,
      value: normalizeRuleValueForOperator(operator, selectedRuleCondition.value),
    });
  }, [selectedRuleCondition.value, updatePrimaryCondition]);

  const updateRuleField = React.useCallback((field) => {
    const nextField = strategyFields.find((item) => item.id === field);
    const defaultOperator = nextField?.type === 'text' ? 'eq' : 'lt';
    const defaultValue = nextField?.type === 'text'
      ? 'positive'
      : normalizeRuleValueForOperator(defaultOperator, selectedRuleCondition.value);
    updatePrimaryCondition({
      field,
      operator: defaultOperator,
      value: defaultValue,
    });
  }, [selectedRuleCondition.value, strategyFields, updatePrimaryCondition]);

  const onInstrumentTypeChange = React.useCallback((value) => {
    if (value === 'option') {
      setForm((prev) => {
        const base = {
          ...prev,
          instrumentType: value,
          strategyMode: STRATEGY_MODE_PREDICTION,
          ruleDefinition: cloneRuleDefinition(defaultRuleDefinition),
        };
        const templateId = prev.optionTemplateId && prev.optionTemplateId !== OPTION_TEMPLATE_CUSTOM
          ? prev.optionTemplateId
          : OPTION_TEMPLATE_BALANCED;
        return {
          ...base,
          ...optionTemplatePatch(base, templateId),
        };
      });
      setStrategyNotice('');
      setBacktestError('');
      setDirty(true);
    } else {
      setForm((prev) => ({
        ...prev,
        instrumentType: value,
        optionSymbol: '',
        autoReselectOptionContract: false,
      }));
      setOptionAutoSelectionSignature('');
      setSelectedOptionContractDetails(null);
      setStrategyNotice('');
      setBacktestError('');
      setDirty(true);
    }
    if (value === 'option' && !optionsRiskAcknowledged) {
      setOptionsRiskChecked(false);
      setShowOptionsRiskModal(true);
    }
  }, [optionsRiskAcknowledged]);

  const cancelOptionsRiskAcknowledgement = React.useCallback(() => {
    setShowOptionsRiskModal(false);
    setOptionsRiskChecked(false);
    if (!optionsRiskAcknowledged) {
      setForm((prev) => ({
        ...prev,
        instrumentType: 'stock',
        optionSymbol: '',
        autoReselectOptionContract: false,
      }));
      setOptionAutoSelectionSignature('');
      setSelectedOptionContractDetails(null);
    }
  }, [optionsRiskAcknowledged]);

  const confirmOptionsRiskAcknowledgement = React.useCallback(async () => {
    if (!optionsRiskChecked || optionsRiskAcceptLoading) return;
    const response = await acceptOptionsRiskAcknowledgement({});
    if (!response?.success || !response?.data?.acknowledged) return;
    await loadOptionsRiskAcknowledgement();
    setShowOptionsRiskModal(false);
    setOptionsRiskChecked(false);
  }, [acceptOptionsRiskAcknowledgement, loadOptionsRiskAcknowledgement, optionsRiskAcceptLoading, optionsRiskChecked]);

  React.useEffect(() => {
    const selectedName = String(form.savedStrategyName || '');
    if (!selectedName) {
      setForm((prev) => ({
        ...prev,
        strategyName: '',
        active: false,
        emailSuggestedAction: false,
        makePublic: false,
        showParametersPublicly: false,
        allowClone: false,
        instrumentType: 'stock',
        strategyMode: STRATEGY_MODE_PREDICTION,
        ruleDefinition: cloneRuleDefinition(defaultRuleDefinition),
        stockTemplateId: STOCK_TEMPLATE_CUSTOM,
        optionTemplateId: OPTION_TEMPLATE_BALANCED,
        optionSymbol: '',
        autoReselectOptionContract: false,
      }));
      setOptionAutoSelectionSignature('');
      setSelectedOptionContractDetails(null);
      setStrategyNotice('');
      setBacktestError('');
      setDirty(false);
      return;
    }

    const selected = visibleSavedStrategies.find((s) => s.name === selectedName);
    if (!selected) return;

    const publication = strategyMarketplaceEnabled ? marketplaceByStrategyId[Number(selected.id)] : null;

    const savedOptionSettings = {
      optionType: selected.option_type || 'call',
      optionActionMode: selected.option_action_mode || optionActionModeForType(selected.option_type),
      optionPriceModel: selected.option_price_model || 'bid_ask',
      optionMaxSpreadPct: selected.option_max_spread_pct == null ? 35 : selected.option_max_spread_pct,
      optionMinVolume: selected.option_min_volume == null ? '' : String(selected.option_min_volume),
      optionMinOpenInterest: selected.option_min_open_interest == null ? '' : String(selected.option_min_open_interest),
      autoReselectOptionContract: Boolean(selected.auto_reselect_option_contract),
    };
    const savedTemplateId = selected.option_template_id && selected.option_template_id !== OPTION_TEMPLATE_CUSTOM
      ? selected.option_template_id
      : inferOptionTemplateId(savedOptionSettings);
    const savedStockTemplateId = inferStockTemplateId({
      tradeFrequencyBdays: selected.trade_frequency_bdays || 1,
      upAction: selected.up_action || 'buy',
      downAction: selected.down_action || 'hold',
      sentimentCondition: (() => {
        const unified = selected.sentiment_condition || selected.sentimentCondition;
        if (unified) return unified;
        const up = selected.up_sentiment_condition;
        const down = selected.down_sentiment_condition;
        if (up && down && up !== down) return 'any';
        return up || down || 'any';
      })(),
      missingSentimentBehavior: selected.missing_sentiment_behavior || 'treat_neutral',
      minPredictedReturn: selected.min_predicted_return == null ? '' : String(selected.min_predicted_return),
      maxPredictedReturn: selected.max_predicted_return == null ? '' : String(selected.max_predicted_return),
    });
    const savedStrategyMode = customStrategiesEnabled
      ? (selected.strategy_mode || STRATEGY_MODE_PREDICTION)
      : STRATEGY_MODE_PREDICTION;

    setForm((prev) => ({
      ...prev,
      strategyName: selected.name || '',
      active: Boolean(selected.active),
      emailSuggestedAction: Boolean(selected.email_suggested_action),
      makePublic: Boolean(publication?.is_public),
      showParametersPublicly: Boolean(publication?.show_parameters),
      allowClone: Boolean(publication?.allow_clone),
      ticker: selected.ticker || '',
      horizon: selected.horizon || 'daily',
      fromDate: selected.from_date || prev.fromDate,
      toDate: selected.to_date || prev.toDate,
      tradeFrequencyBdays: selected.trade_frequency_bdays || 1,
      upAction: selected.up_action || 'buy',
      downAction: selected.down_action || 'hold',
      sentimentCondition: (() => {
        const unified = selected.sentiment_condition || selected.sentimentCondition;
        if (unified) return unified;
        const up = selected.up_sentiment_condition;
        const down = selected.down_sentiment_condition;
        if (up && down && up !== down) return 'any';
        return up || down || 'any';
      })(),
      missingSentimentBehavior: selected.missing_sentiment_behavior || 'treat_neutral',
      minPredictedReturn: selected.min_predicted_return == null ? '' : String(selected.min_predicted_return),
      maxPredictedReturn: selected.max_predicted_return == null ? '' : String(selected.max_predicted_return),
      initialCapital: selected.initial_capital == null ? '' : String(selected.initial_capital),
      instrumentType: optionsStrategiesEnabled && selected.instrument_type === 'option' ? 'option' : 'stock',
      strategyMode: savedStrategyMode,
      ruleDefinition: normalizeRuleDefinitionForForm(selected.rule_definition),
      stockTemplateId: savedStrategyMode === STRATEGY_MODE_PREDICTION ? savedStockTemplateId : STOCK_TEMPLATE_CUSTOM,
      optionType: savedOptionSettings.optionType,
      optionTemplateId: savedTemplateId,
      optionActionMode: savedOptionSettings.optionActionMode,
      optionPriceModel: savedOptionSettings.optionPriceModel,
      optionSymbol: selected.option_symbol || '',
      autoReselectOptionContract: savedOptionSettings.autoReselectOptionContract,
      optionMaxSpreadPct: savedOptionSettings.optionMaxSpreadPct,
      optionMinVolume: savedOptionSettings.optionMinVolume,
      optionMinOpenInterest: savedOptionSettings.optionMinOpenInterest,
    }));
    setOptionAutoSelectionSignature('');
    setSelectedOptionContractDetails(null);
    setStrategyNotice(selected.initial_capital == null
      ? 'This saved strategy has no initial capital. Enter an amount before saving or running.' : '');
    setBacktestError('');
    setDirty(false);
  }, [customStrategiesEnabled, form.savedStrategyName, marketplaceByStrategyId, optionsStrategiesEnabled, strategyMarketplaceEnabled, visibleSavedStrategies]);

  const canRunBlockReason = React.useMemo(() => {
    if (numericValidation.error) return numericValidation.error;
    if (!form.ticker) return 'Select a ticker.';
    if (!form.horizon) return 'Select a prediction horizon.';
    if (!form.fromDate || !form.toDate) return 'Select a date range.';
    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Select a valid date range.';
    if (from > to) return 'From Date must be before To Date.';
    if (form.instrumentType === 'option') {
      if (!optionsStrategiesEnabled) return 'Options backtesting is disabled.';
      if (!optionsRiskAcknowledged) return 'Acknowledge options risk before running an options backtest.';
      if (!form.optionSymbol && !form.autoReselectOptionContract) {
        return 'Select a historical option contract or enable re-selection.';
      }
    }
    if (form.instrumentType === 'stock' && form.strategyMode !== STRATEGY_MODE_PREDICTION) {
      if (!customStrategiesEnabled) return 'Custom strategy rules are disabled.';
      if (!selectedRuleCondition?.field || !ruleFieldsForMode.some((field) => field.id === selectedRuleCondition.field)) {
        return 'Select a custom rule field.';
      }
      if (!selectedRuleCondition?.operator) return 'Select a custom rule operator.';
      if (!ruleOperatorsForField.some((operator) => operator.value === selectedRuleCondition.operator)) {
        return 'Select a valid custom rule operator.';
      }
      if (operatorNeedsValue(selectedRuleCondition.operator)) {
        const value = selectedRuleCondition.value;
        if (selectedRuleCondition.operator === 'between') {
          if (!Array.isArray(value) || value.some((entry) => String(entry ?? '').trim() === '')) {
            return 'Enter both custom rule values.';
          }
        } else if (String(value ?? '').trim() === '') {
          return 'Enter a custom rule value.';
        }
      }
    }
    return '';
  }, [numericValidation.error, customStrategiesEnabled, form, optionsRiskAcknowledged, optionsStrategiesEnabled, ruleFieldsForMode, ruleOperatorsForField, selectedRuleCondition]);

  const canRun = !canRunBlockReason;

  const activationIssues = React.useMemo(
    () => getStrategyActivationIssues(form, optionsRiskAcknowledged),
    [form, optionsRiskAcknowledged],
  );

  const activationIssueText = React.useMemo(
    () => formatActivationIssues(activationIssues),
    [activationIssues],
  );

  const dateError = React.useMemo(() => {
    if (!form.fromDate || !form.toDate) return null;
    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 'Invalid date';
    if (from > to) return 'From Date must be before To Date';
    return null;
  }, [form.fromDate, form.toDate]);

  const customStrategySaveUnavailable = React.useMemo(() => (
    customStrategiesEnabled &&
    form.instrumentType === 'stock' &&
    form.strategyMode !== STRATEGY_MODE_PREDICTION &&
    !customStrategyPersistenceEnabled
  ), [customStrategiesEnabled, customStrategyPersistenceEnabled, form.instrumentType, form.strategyMode]);

  const saveBlockReason = React.useMemo(() => {
    if (numericValidation.error) return numericValidation.error;
    const name = String(form.strategyName || '').trim();
    if (!name) return 'Strategy name is required';
    if (customStrategySaveUnavailable) {
      return 'Custom rule saving requires the custom-rule database migration. Run Backtest is available.';
    }
    if (saveLoading) return 'Saving strategy';
    if (strategiesLoading) return 'Loading saved strategies';
    if (marketplacePublishLoading || marketplaceUnpublishLoading) return 'Updating marketplace status';
    return '';
  }, [
    customStrategySaveUnavailable,
    numericValidation.error,
    form.strategyName,
    marketplacePublishLoading,
    marketplaceUnpublishLoading,
    saveLoading,
    strategiesLoading,
  ]);

  const saveDisabled = Boolean(saveBlockReason);

  const backtestBusy = Boolean(backtestLoading || optionsBacktestLoading);

  const onSave = React.useCallback(async () => {
    if (numericValidation.error) return;
    const name = String(form.strategyName || '').trim();
    if (!name) return;
    const currentActivationIssues = getStrategyActivationIssues(form, optionsRiskAcknowledged);
    const canSaveActive = currentActivationIssues.length === 0;
    const saveAsActive = Boolean(form.active && canSaveActive);
    const saveAsPublic = Boolean(form.makePublic && canSaveActive);
    const effectiveRuleDefinition = effectiveStrategyMode === STRATEGY_MODE_PREDICTION
      ? null
      : normalizeRuleDefinitionForForm(form.ruleDefinition);

    const saveResp = await saveStrategyApi({
      name,
      active: saveAsActive,
      email_suggested_action: Boolean(form.emailSuggestedAction),
      ticker: form.ticker,
      horizon: form.horizon,
      from_date: form.fromDate,
      to_date: form.toDate,
      trade_frequency_bdays: Number(form.tradeFrequencyBdays) || 1,
      up_action: form.upAction,
      down_action: form.downAction,
      sentiment_condition: form.sentimentCondition,
      missing_sentiment_behavior: form.missingSentimentBehavior,
      ...numericValidation.values,
      instrument_type: form.instrumentType,
      strategy_mode: effectiveStrategyMode,
      rule_definition: effectiveRuleDefinition,
      option_template_id: form.instrumentType === 'option' ? form.optionTemplateId : null,
      option_symbol: form.instrumentType === 'option' ? form.optionSymbol : null,
      option_type: form.instrumentType === 'option' ? form.optionType : null,
      option_action_mode: form.instrumentType === 'option' ? form.optionActionMode : null,
      auto_reselect_option_contract: form.instrumentType === 'option' ? Boolean(form.autoReselectOptionContract) : false,
      option_price_model: form.instrumentType === 'option' ? form.optionPriceModel : null,
      option_max_spread_pct: form.instrumentType === 'option' && String(form.optionMaxSpreadPct || '').trim() !== '' ? Number(form.optionMaxSpreadPct) : null,
      option_min_volume: form.instrumentType === 'option' && String(form.optionMinVolume || '').trim() !== '' ? Number(form.optionMinVolume) : null,
      option_min_open_interest: form.instrumentType === 'option' && String(form.optionMinOpenInterest || '').trim() !== '' ? Number(form.optionMinOpenInterest) : null,
    });

    if (!saveResp?.success) {
      return;
    }

    const savedList = saveResp?.data?.strategies || [];
    const savedStrategy = savedList.find((s) => String(s?.name || '') === name);
    const strategyId = Number(savedStrategy?.id);
    if (strategyMarketplaceEnabled && Number.isFinite(strategyId) && strategyId > 0) {
      if (saveAsPublic) {
        await publishMarketplaceApi({
          strategy_id: strategyId,
          display_name: name,
          is_public: true,
          show_parameters: Boolean(form.showParametersPublicly),
          allow_clone: Boolean(form.allowClone),
        });
      } else {
        await unpublishMarketplaceApi({ strategy_id: strategyId });
      }
    }

    setDirty(false);
    setForm((prev) => ({
      ...prev,
      savedStrategyName: name,
      active: saveAsActive,
      makePublic: saveAsPublic,
      showParametersPublicly: saveAsPublic ? prev.showParametersPublicly : false,
      allowClone: saveAsPublic ? prev.allowClone : false,
    }));
    if (form.active && !saveAsActive) {
      setStrategyNotice(`Saved as draft. Add ${formatActivationIssues(currentActivationIssues)} before marking it Active.`);
    } else if (form.makePublic && !saveAsPublic) {
      setStrategyNotice(`Saved as draft. Add ${formatActivationIssues(currentActivationIssues)} before publishing it.`);
    } else {
      setStrategyNotice('Strategy saved.');
    }
    if (strategyMarketplaceEnabled) {
      await Promise.all([loadStrategies(), loadMarketplaceMine()]);
    } else {
      await loadStrategies();
    }
  }, [numericValidation, effectiveStrategyMode, form, loadMarketplaceMine, loadStrategies, optionsRiskAcknowledged, publishMarketplaceApi, saveStrategyApi, strategyMarketplaceEnabled, unpublishMarketplaceApi]);

  const buildBacktestPayload = React.useCallback(() => {
    const effectiveRuleDefinition = effectiveStrategyMode === STRATEGY_MODE_PREDICTION
      ? null
      : normalizeRuleDefinitionForForm(form.ruleDefinition);
    const useStockPredictionTemplate = form.instrumentType === 'stock' && effectiveStrategyMode === STRATEGY_MODE_PREDICTION;

    return {
      ticker: form.ticker,
      horizon: form.horizon,
      from_date: form.fromDate,
      to_date: form.toDate,
      trade_frequency_bdays: Number(form.tradeFrequencyBdays) || 1,
      up_action: form.upAction,
      down_action: form.downAction,
      sentiment_condition: form.sentimentCondition,
      missing_sentiment_behavior: form.missingSentimentBehavior,
      ...numericValidation.values,
      strategy_mode: effectiveStrategyMode,
      rule_definition: effectiveRuleDefinition,
      stock_template_id: useStockPredictionTemplate ? form.stockTemplateId : null,
      include_template_comparison: useStockPredictionTemplate,
      include_benchmark: true,
      benchmark_ticker: 'GSPC',
    };
  }, [numericValidation.values, effectiveStrategyMode, form.downAction, form.fromDate, form.horizon, form.instrumentType, form.missingSentimentBehavior, form.ruleDefinition, form.sentimentCondition, form.stockTemplateId, form.ticker, form.toDate, form.tradeFrequencyBdays, form.upAction]);

  const buildOptionsBacktestPayload = React.useCallback((overrides = {}) => ({
    ...buildBacktestPayload(),
    option_symbol: form.optionSymbol || null,
    auto_select_contract: Boolean(form.autoReselectOptionContract || overrides.auto_select_contract),
    auto_reselect_contract: Boolean(form.autoReselectOptionContract || overrides.auto_reselect_contract),
    option_type: form.optionType,
    option_action_mode: form.optionActionMode,
    option_price_model: form.optionPriceModel,
    option_max_spread_pct: String(form.optionMaxSpreadPct || '').trim() === '' ? null : Number(form.optionMaxSpreadPct),
    option_min_volume: String(form.optionMinVolume || '').trim() === '' ? null : Number(form.optionMinVolume),
    option_min_open_interest: String(form.optionMinOpenInterest || '').trim() === '' ? null : Number(form.optionMinOpenInterest),
    sync_if_missing: true,
    include_template_comparison: true,
    ...overrides,
  }), [buildBacktestPayload, form.autoReselectOptionContract, form.optionActionMode, form.optionMaxSpreadPct, form.optionMinOpenInterest, form.optionMinVolume, form.optionPriceModel, form.optionSymbol, form.optionType]);

  const onRunBacktest = React.useCallback(async () => {
    if (numericValidation.error) return;
    const payload = buildBacktestPayload();
    const data = form.instrumentType === 'option'
      ? await runOptionsBacktest(buildOptionsBacktestPayload())
      : await runBacktest(payload);
    if (!data?.success) {
      const message = data?.message || data?.error || 'Backtest failed. Please try again.';
      setResult(undefined);
      setStrategyNotice(message);
      setBacktestError(message);
      return;
    }
    setResult(data?.data);
    setStrategyNotice('');
    setBacktestError('');
    const selectedSymbol = data?.data?.option_strategy?.option_symbol;
    if (form.instrumentType === 'option' && selectedSymbol) {
      setForm((prev) => ({ ...prev, optionSymbol: selectedSymbol }));
      const selectedContract = data?.data?.option_contract_selection?.selected_contract;
      setSelectedOptionContractDetails(selectedContract || { option_symbol: selectedSymbol });
      const selectedContractText = formatContractSummary(selectedContract);
      if (form.autoReselectOptionContract) {
        setOptionAutoSelectionSignature(currentOptionAutoSelectionSignature);
        setOptionContractsMessage(selectedContractText ? `Re-selected ${selectedContractText}.` : `Re-selected ${selectedSymbol}.`);
      }
    }
  }, [numericValidation.error, buildBacktestPayload, buildOptionsBacktestPayload, currentOptionAutoSelectionSignature, form.autoReselectOptionContract, form.instrumentType, runBacktest, runOptionsBacktest]);

  const onLoadOptionContracts = React.useCallback(async () => {
    if (!form.ticker || !form.fromDate || !form.toDate) return;
    const queryKey = currentOptionContractsQueryKey;
    setOptionContractsMessage('Loading latest option history…');
    const params = new URLSearchParams();
    params.set('ticker', form.ticker);
    params.set('option_type', form.optionType);
    params.set('from_date', form.fromDate);
    params.set('to_date', form.toDate);
    params.set('limit', '250');
    params.set('refresh', '1');
    params.set('max_pages', '1');
    const response = await loadOptionContractsApi(undefined, { urlPath: `?${params.toString()}` });
    if (!response?.success) {
      if (currentOptionContractsQueryKeyRef.current !== queryKey) return;
      setOptionContractsQueryKey('');
      setOptionContractsMessage(response?.message || response?.error || 'Failed to load option contracts.');
      return;
    }
    const count = Number(response?.data?.contracts?.length || 0);
    if (currentOptionContractsQueryKeyRef.current !== queryKey) return;
    setOptionContractsQueryKey(queryKey);
    setOptionContractsMessage(count ? `${count} latest available contracts loaded.` : 'No available contracts found for this ticker and date window.');
  }, [currentOptionContractsQueryKey, form.fromDate, form.optionType, form.ticker, form.toDate, loadOptionContractsApi]);

  const onAutoSelectOptionContract = React.useCallback(async () => {
    if (numericValidation.error) return;
    if (!canAutoSelectOptionContract) return;
    setOptionContractsMessage('Selecting an available contract for these settings…');
    const response = await selectOptionsContractApi(buildOptionsBacktestPayload({
      option_symbol: null,
      auto_select_contract: true,
      auto_reselect_contract: true,
    }));
    if (!response?.success) {
      setOptionContractsMessage(response?.message || response?.error || 'Failed to auto-select option contract.');
      return;
    }
    const data = response?.data || {};
    const selectedSymbol = data?.option_strategy?.option_symbol;
    const selectedContract = data?.option_contract_selection?.selected_contract;
    const selectedContractText = formatContractSummary(selectedContract);
    if (!selectedSymbol) {
      setOptionContractsMessage('No available contract could be selected for these settings.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      optionSymbol: selectedSymbol,
    }));
    setSelectedOptionContractDetails(selectedContract || { option_symbol: selectedSymbol });
    setOptionAutoSelectionSignature(currentOptionAutoSelectionSignature);
    const prefix = data?.option_contract_selection?.selected === false ? 'Closest evaluated contract' : 'Auto-selected';
    setOptionContractsMessage(selectedContractText ? `${prefix}: ${selectedContractText}.` : `${prefix}: ${selectedSymbol}.`);
  }, [numericValidation.error, buildOptionsBacktestPayload, canAutoSelectOptionContract, currentOptionAutoSelectionSignature, selectOptionsContractApi]);

  const rows = React.useMemo(() => {
    const list = result?.rows || [];
    return list.map((r, idx) => ({
      id: r.Row ?? idx + 1,
      ...r,
    }));
  }, [result]);
  const summary = result?.summary;
  const comparison = React.useMemo(() => result?.comparison || [], [result]);

  const stockColumns = React.useMemo(() => ([
    { name: 'Row', type: 'number', hideable: false, minWidth: 50, textAlign: 'center', headerAlign: 'center' },
    { name: 'EntryDate', type: 'string', hideable: false, minWidth: 100, textAlign: 'center', headerAlign: 'center' },
    { name: 'ExitDate', type: 'string', hideable: false, minWidth: 100, textAlign: 'center', headerAlign: 'center' },
    {
      name: 'SignalReturnPct',
      header: 'Predicted Return %',
      type: 'number',
      hideable: false,
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'SignalLabel',
      header: 'Rule Signal',
      type: 'string',
      hideable: false,
      minWidth: 130,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => value || '—',
    },
    {
      name: 'SignalScore',
      header: 'Signal Score',
      type: 'number',
      hideable: false,
      minWidth: 105,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => {
        const number = Number(value);
        return Number.isFinite(number) ? number.toFixed(2) : '—';
      },
    },
    {
      name: 'Action',
      type: 'string',
      hideable: false,
      minWidth: 90,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => {
        const v = String(value || '').toLowerCase();
        if (v === 'buy' || v === 'long') return 'Buy';
        if (v === 'sell') return 'Sell';
        if (v === 'short') return 'Short';
        return 'No Trade';
      },
    },
    {
      name: 'RealizedReturnPct',
      header: 'Realized Return %',
      type: 'number',
      hideable: false,
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'TradeReturnPct',
      header: 'Trade Return %',
      type: 'number',
      hideable: false,
      minWidth: 100,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'Equity',
      type: 'number',
      hideable: false,
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatMoney(value),
    },
    {
      name: 'CumulativeReturnPct',
      header: 'Cumulative Return %',
      type: 'number',
      hideable: false,
      minWidth: 120,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'PredictedPrice',
      header: 'Predicted Price',
      type: 'number',
      hideable: false,
      minWidth: 160,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => (Number.isFinite(value) ? Number(value).toFixed(4) : '—'),
    },
  ]), []);

  const optionColumns = React.useMemo(() => ([
    { name: 'Row', type: 'number', hideable: false, minWidth: 50, textAlign: 'center', headerAlign: 'center' },
    { name: 'EntryDate', type: 'string', hideable: false, minWidth: 100, textAlign: 'center', headerAlign: 'center' },
    { name: 'ExitDate', type: 'string', hideable: false, minWidth: 100, textAlign: 'center', headerAlign: 'center' },
    { name: 'OptionSymbol', header: 'Option Contract', type: 'string', hideable: false, minWidth: 180, textAlign: 'center', headerAlign: 'center' },
    {
      name: 'UnderlyingAction',
      header: 'Stock Signal',
      type: 'string',
      hideable: false,
      minWidth: 100,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => {
        const v = String(value || '').toLowerCase();
        if (v === 'buy') return 'Buy';
        if (v === 'sell') return 'Sell';
        if (v === 'short') return 'Short';
        return 'No Trade';
      },
    },
    {
      name: 'OptionAction',
      header: 'Entry Action',
      type: 'string',
      hideable: false,
      minWidth: 120,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value, data }) => <OptionActionPill value={data?.OptionEntryAction || value} />,
    },
    {
      name: 'OptionExitAction',
      header: 'Exit Action',
      type: 'string',
      hideable: false,
      minWidth: 120,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => <OptionActionPill value={value} />,
    },
    {
      name: 'SignalReturnPct',
      header: 'Predicted Return %',
      type: 'number',
      hideable: false,
      minWidth: 120,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'OptionEntryPrice',
      header: 'Entry Option Price',
      type: 'number',
      hideable: false,
      minWidth: 125,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => (Number.isFinite(value) ? Number(value).toFixed(4) : '—'),
    },
    {
      name: 'OptionExitPrice',
      header: 'Exit Option Price',
      type: 'number',
      hideable: false,
      minWidth: 125,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => (Number.isFinite(value) ? Number(value).toFixed(4) : '—'),
    },
    {
      name: 'OptionContractCost',
      header: '1 Contract Cost',
      type: 'number',
      hideable: false,
      minWidth: 125,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatDollars(value),
    },
    {
      name: 'OptionAffordableContracts',
      header: 'Affordable',
      type: 'number',
      hideable: false,
      minWidth: 95,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => (Number.isFinite(value) ? Number(value).toFixed(0) : '—'),
    },
    { name: 'OptionContracts', header: 'Contracts', type: 'number', hideable: false, minWidth: 95, textAlign: 'center', headerAlign: 'center' },
    {
      name: 'OptionNotional',
      header: 'Capital Used',
      type: 'number',
      hideable: false,
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatMoney(value),
    },
    {
      name: 'OptionPriceReturnPct',
      header: 'Option Price Return %',
      type: 'number',
      hideable: false,
      minWidth: 130,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'TradeReturnPct',
      header: 'Portfolio Trade Return %',
      type: 'number',
      hideable: false,
      minWidth: 150,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'Equity',
      type: 'number',
      hideable: false,
      minWidth: 110,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatMoney(value),
    },
    {
      name: 'CumulativeReturnPct',
      header: 'Cumulative Return %',
      type: 'number',
      hideable: false,
      minWidth: 130,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value }) => formatPct(value),
    },
    {
      name: 'SkipReasonLabel',
      header: 'Skip Reason',
      type: 'string',
      hideable: false,
      minWidth: 170,
      textAlign: 'center',
      headerAlign: 'center',
      render: ({ value, data }) => value || formatSkipReason(data?.SkipReason),
    },
    {
      name: 'SkipReasonDetail',
      header: 'Skip Detail',
      type: 'string',
      hideable: false,
      minWidth: 320,
      textAlign: 'left',
      headerAlign: 'center',
      render: ({ value }) => value || '—',
    },
  ]), []);

  const isOptionsResult = result?.instrument_type === 'option';
  const columns = isOptionsResult ? optionColumns : stockColumns;
  const marketBenchmark = result?.market_benchmark;
  const optionSelection = result?.option_contract_selection;
  const optionsAttribution = result?.options_attribution;
  const stockAttribution = result?.stock_attribution;
  const stockTemplateResults = React.useMemo(
    () => (Array.isArray(result?.stock_template_results) ? result.stock_template_results : []),
    [result?.stock_template_results],
  );
  const optionTemplateResults = React.useMemo(
    () => (Array.isArray(result?.option_template_results) ? result.option_template_results : []),
    [result?.option_template_results],
  );
  const optionSkipReasonEntries = React.useMemo(() => {
    const reasons = summary?.n_skipped_by_reason || {};
    return Object.entries(reasons)
      .filter(([, count]) => Number(count) > 0)
      .sort((a, b) => Number(b[1]) - Number(a[1]));
  }, [summary?.n_skipped_by_reason]);
  const optionSelectedContractText = formatContractSummary(optionSelection?.selected_contract);
  const optionSignalCount = Number(summary?.n_option_signals || 0);
  const optionExecutedCount = Number(summary?.n_executed_trades || summary?.n_buy || 0);
  const buyHoldReturnPct = React.useMemo(() => {
    if (!isOptionsResult) {
      return summary?.buy_hold_return_pct == null ? null : Number(summary.buy_hold_return_pct);
    }

    const buyHoldComparison = comparison.find((item) => {
      const label = String(item?.label || '').trim().toLowerCase();
      const instrumentType = String(item?.instrument_type || '').trim().toLowerCase();
      return label === 'buy & hold' || instrumentType === 'stock';
    });
    const value = result?.stock_summary?.buy_hold_return_pct ?? buyHoldComparison?.total_return_pct;
    return value == null ? null : Number(value);
  }, [comparison, isOptionsResult, result?.stock_summary?.buy_hold_return_pct, summary?.buy_hold_return_pct]);

  const defaultSortInfo = React.useMemo(() => ([
    { name: 'Row', dir: 1 },
  ]), []);

  const defaultFilterValue = React.useMemo(
    () => columns.map(({ name, type }) => ({
      name,
      type,
      operator: type === 'number' ? 'inrange' : 'contains',
      value: null,
    })),
    [columns],
  );

  const stopTableResize = React.useCallback(() => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    tableResizeStateRef.current.active = false;
    clearStrategiesResizeBodyClasses();
  }, []);

  const moveTableResize = React.useCallback((event) => {
    if (!tableResizeStateRef.current.active) {
      return;
    }
    const { mode } = tableResizeStateRef.current;
    if (mode === STRATEGIES_RESIZE_MODE_Y || mode === STRATEGIES_RESIZE_MODE_BOTH) {
      const deltaY = event.clientY - tableResizeStateRef.current.startY;
      const nextHeight = clampStrategiesTableHeight(tableResizeStateRef.current.startHeight + deltaY);
      setTablePanelHeight(nextHeight);
    }
    if (mode === STRATEGIES_RESIZE_MODE_X || mode === STRATEGIES_RESIZE_MODE_BOTH) {
      const containerWidth = Number(tableResizeStateRef.current.startContainerWidth);
      if (Number.isFinite(containerWidth) && containerWidth > 0) {
        const deltaX = event.clientX - tableResizeStateRef.current.startX;
        const deltaPct = (deltaX / containerWidth) * 100;
        const nextWidth = clampStrategiesPanelWidthPercent(tableResizeStateRef.current.startWidthPercent + deltaPct);
        setResultPanelsWidthPercent(nextWidth);
      }
    }
    event.preventDefault();
  }, []);

  const startTableResize = React.useCallback((event, mode) => {
    if (event.button !== 0) {
      return;
    }
    const parentWidth = Number(resultsStackRef.current?.parentElement?.clientWidth);
    const fallbackWidth = Number(resultsStackRef.current?.clientWidth) || Number(tradesCardRef.current?.clientWidth);
    const startContainerWidth = Number.isFinite(parentWidth) && parentWidth > 0
      ? parentWidth
      : (Number.isFinite(fallbackWidth) && fallbackWidth > 0 ? fallbackWidth : 1);
    tableResizeStateRef.current.active = true;
    tableResizeStateRef.current.mode = mode;
    tableResizeStateRef.current.startX = event.clientX;
    tableResizeStateRef.current.startY = event.clientY;
    tableResizeStateRef.current.startContainerWidth = startContainerWidth;
    tableResizeStateRef.current.startHeight = tablePanelHeight;
    tableResizeStateRef.current.startWidthPercent = resultPanelsWidthPercent;
    setStrategiesResizeBodyClass(mode);
    event.preventDefault();
  }, [resultPanelsWidthPercent, tablePanelHeight]);

  const startTableResizeY = React.useCallback((event) => {
    startTableResize(event, STRATEGIES_RESIZE_MODE_Y);
  }, [startTableResize]);

  const startTableResizeX = React.useCallback((event) => {
    startTableResize(event, STRATEGIES_RESIZE_MODE_X);
  }, [startTableResize]);

  const startTableResizeBoth = React.useCallback((event) => {
    startTableResize(event, STRATEGIES_RESIZE_MODE_BOTH);
  }, [startTableResize]);

  const resetPanelSize = React.useCallback(() => {
    setTablePanelHeight(STRATEGIES_TABLE_DEFAULT_HEIGHT);
    setResultPanelsWidthPercent(STRATEGIES_PANEL_DEFAULT_WIDTH_PERCENT);
  }, []);

  React.useEffect(() => {
    window.addEventListener('mousemove', moveTableResize);
    window.addEventListener('mouseup', stopTableResize);
    return () => {
      window.removeEventListener('mousemove', moveTableResize);
      window.removeEventListener('mouseup', stopTableResize);
      clearStrategiesResizeBodyClasses();
    };
  }, [moveTableResize, stopTableResize]);

  const exportColumns = React.useMemo(() => (
    columns.map((c) => ({ name: c.name, header: c.header || c.name }))
  ), [columns]);

  const gridStyle = React.useMemo(() => ({
    width: '100%',
    height: '100%',
  }), []);

  const onExportCsv = React.useCallback(() => {
    if (!rows.length) return;
    const safeTicker = String(form.ticker || '').trim().toUpperCase() || 'TICKER';
    const safeHorizon = String(form.horizon || 'daily').trim() || 'daily';
    const safeFrom = String(form.fromDate || '').slice(0, 10) || 'from';
    const safeTo = String(form.toDate || '').slice(0, 10) || 'to';

    const safeInstrument = isOptionsResult ? 'options' : 'stock';
    const filename = `backtest_${safeInstrument}_${safeTicker}_${safeHorizon}_${safeFrom}_${safeTo}.csv`;
    const csv = buildCsv({ columns: exportColumns, rows });
    downloadTextFile({ contents: csv, filename, mimeType: 'text/csv;charset=utf-8' });
  }, [exportColumns, form.fromDate, form.horizon, form.ticker, form.toDate, isOptionsResult, rows]);

  const savedStrategyOptions = React.useMemo(() => ([
    { value: '', label: 'New strategy…' },
    ...visibleSavedStrategies.map((s) => ({
      value: s.name,
      label: `${s.name}${s.active ? ' (Active)' : ''}`,
    })),
  ]), [visibleSavedStrategies]);

  const tickers = React.useMemo(
    () => (tickersPayload?.data || []).map((t) => String(t)),
    [tickersPayload],
  );

  const activeRecommendationStrategyName = String(activeRecommendation?.strategy?.name || activeStrategyName || '').trim();
  const selectedSavedStrategyName = String(form.savedStrategyName || '').trim();
  const showActiveRecommendation = Boolean(
    activeRecommendation &&
    activeRecommendationStrategyName &&
    selectedSavedStrategyName &&
    selectedSavedStrategyName === activeRecommendationStrategyName,
  );
  const usingRuleStrategy = Boolean(
    customStrategiesEnabled &&
    form.instrumentType === 'stock' &&
    form.strategyMode !== STRATEGY_MODE_PREDICTION,
  );
  const usingPredictionSettings = Boolean(
    form.instrumentType !== 'stock' ||
    form.strategyMode === STRATEGY_MODE_PREDICTION,
  );
  const selectedBetweenValue = Array.isArray(selectedRuleCondition.value)
    ? selectedRuleCondition.value
    : ['', ''];
  const selectedRuleValueText = Array.isArray(selectedRuleCondition.value)
    ? String(selectedRuleCondition.value[0] ?? '')
    : String(selectedRuleCondition.value ?? '');

  return (
    <div className="stockSec dt-stockSec-strategies">
      <div className="container-fluid">
        <DashboardMenu />

        <div className="dt-strategies-page">
          <div className="dt-page-head">
            <div className="dt-page-head-left">
              <h1 className="dt-page-title">Trading Strategies</h1>
              <p className="dt-page-subtitle">
                {customStrategiesEnabled
                  ? 'Create stock strategies from DeepTrading predictions, custom rules, or hybrid signals, then run a backtest to see the trades and equity curve.'
                  : 'Create a simple rules-based strategy on DeepTrading predictions, then run a backtest to see the trades and equity curve.'}
              </p>
            </div>
          </div>

          {showActiveRecommendation && (
            <div className="dt-callout">
              <div className="dt-callout-meta">
                <span className="dt-callout-label">Active Strategy:</span>
                <span className="dt-callout-value">
                  {activeStrategyName || activeRecommendation?.strategy?.name || '—'}
                </span>
                <span className="dt-callout-sep">|</span>
                <span className="dt-callout-label">As Of:</span>
                <span className="dt-callout-value">{activeRecommendation.as_of_date || '—'}</span>
                <span className="dt-callout-sep">|</span>
                <span className="dt-callout-label">Predicted:</span>
                <span className="dt-callout-value">{formatPct(activeRecommendation.predicted_return_pct)}</span>
                {activeRecommendation.signal_label ? (
                  <>
                    <span className="dt-callout-sep">|</span>
                    <span className="dt-callout-label">Signal:</span>
                    <span className="dt-callout-value">{activeRecommendation.signal_label}</span>
                  </>
                ) : null}
              </div>

              <div className="dt-callout-main">
                <span className="dt-callout-main-label">Suggested Trading Action</span>
                <span className={`dt-callout-action dt-callout-action--${recommendationActionTone(activeRecommendation.action)}`}>
                  {formatRecommendationAction(activeRecommendation.action)}
                </span>
                <label htmlFor="emailSuggestedActionCallout" className="dt-checkbox-row dt-callout-email-row">
                  <input
                    id="emailSuggestedActionCallout"
                    type="checkbox"
                    className="dt-checkbox"
                    checked={Boolean(form.emailSuggestedAction)}
                    onChange={(e) => updateField('emailSuggestedAction', e.target.checked)}
                  />
                  <span>Email me the suggested trading action</span>
                  <HelpTip id="help-emailSuggestedAction" text={FIELD_HELP.emailSuggestedAction} />
                </label>
              </div>
            </div>
          )}

          <div className="dt-grid">
            <section className="dt-card dt-card-pad dt-card-settings">
              <header className="dt-card-header">
                <div>
                  <h2 className="dt-card-title">Strategy Settings</h2>
                  <div className="dt-card-note">All days are business days (not calendar days).</div>
                </div>

                <div className="dt-card-header-actions">
                  <button
                    type="button"
                    className="dt-btn dt-btn-primary dt-action-btn"
                    onClick={() => setShowHelp(true)}
                  >
                    Help
                  </button>

                  <button
                    type="button"
                    className="dt-btn dt-btn-accent dt-action-btn"
                    onClick={onSave}
                    disabled={saveDisabled}
                    title={saveBlockReason || 'Save strategy'}
                  >
                    {saveLoading ? 'Saving…' : 'Save'}
                  </button>

                  <label className="dt-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(form.active)}
                      onChange={(e) => updateField('active', e.target.checked)}
                    />
                    <span className="dt-toggle-ui" aria-hidden="true" />
                    <span className="dt-toggle-label">
                      Active <HelpTip id="help-active" text={FIELD_HELP.active} className="dt-help-icon--small" />
                    </span>
                  </label>
                </div>
              </header>

              <div className="dt-form">
	                <div className="dt-field">
                  <label htmlFor="savedStrategy">
                    Saved Strategy <HelpTip id="help-savedStrategy" text={FIELD_HELP.savedStrategy} />
                  </label>
                  <select
                    id="savedStrategy"
                    className="dt-input"
                    value={form.savedStrategyName || ''}
                    onChange={(e) => updateField('savedStrategyName', e.target.value)}
                    disabled={strategiesLoading}
                  >
                    {savedStrategyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
	                  </select>
	                  <div className="dt-help">Select a saved strategy to load or start a new one.</div>
	                </div>

                <div className="dt-field dt-field-span2">
                  <label htmlFor="strategyName">
                    Strategy Name <span className="dt-required">*</span>
                    <HelpTip id="help-strategyName" text={FIELD_HELP.strategyName} />
                  </label>
                  <input
                    id="strategyName"
                    className="dt-input"
                    value={form.strategyName}
                    onChange={(e) => updateField('strategyName', e.target.value)}
                    placeholder="e.g., Daily Momentum (No Trade on negative)"
                  />
                </div>

                {optionsStrategiesEnabled ? (
                  <div className="dt-field">
                    <label htmlFor="instrumentType">
                      Instrument <HelpTip id="help-instrumentType" text={FIELD_HELP.instrumentType} />
                    </label>
                    <select
                      id="instrumentType"
                      className="dt-input"
                      value={form.instrumentType}
                      onChange={(e) => onInstrumentTypeChange(e.target.value)}
                    >
                      {instrumentTypes.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
	                    </select>
	                  </div>
	                ) : null}

                  {customStrategiesEnabled && form.instrumentType === 'stock' ? (
                    <div className="dt-field">
                      <label htmlFor="strategyMode">
                        Strategy Mode <HelpTip id="help-strategyMode" text={FIELD_HELP.strategyMode} />
                      </label>
                      <select
                        id="strategyMode"
                        className="dt-input"
                        value={form.strategyMode}
                        onChange={(e) => updateField('strategyMode', e.target.value)}
                      >
                        {strategyModeOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}

	                {form.instrumentType === 'stock' && form.strategyMode === STRATEGY_MODE_PREDICTION ? (
	                  <div className="dt-field">
	                    <label htmlFor="stockTemplateId">
	                      Stock Template <HelpTip id="help-stockTemplate" text={FIELD_HELP.stockTemplate} />
	                    </label>
	                    <select
	                      id="stockTemplateId"
	                      className="dt-input"
	                      value={form.stockTemplateId}
	                      onChange={(e) => updateField('stockTemplateId', e.target.value)}
	                    >
	                      {stockTemplatePresets.map((opt) => (
	                        <option key={opt.value} value={opt.value}>{opt.label}</option>
	                      ))}
	                    </select>
	                    {selectedStockTemplatePreset?.description ? (
	                      <div className="dt-help">{selectedStockTemplatePreset.description}</div>
	                    ) : null}
	                  </div>
	                ) : null}
	                <div className="dt-field">
                  <label htmlFor="ticker">
                    Ticker <span className="dt-required">*</span>
                    <HelpTip id="help-ticker" text={FIELD_HELP.ticker} />
                  </label>
                  <select
                    id="ticker"
                    className="dt-input"
                    value={form.ticker}
                    onChange={(e) => updateField('ticker', e.target.value)}
                  >
                    <option value="">Select ticker…</option>
                    {tickers.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>

                <div className="dt-field">
                  <label htmlFor="horizon">
                    {usingRuleStrategy ? 'Return Horizon' : 'Prediction Horizon'} <span className="dt-required">*</span>
                    <HelpTip id="help-horizon" text={FIELD_HELP.horizon} />
                  </label>
                  <select
                    id="horizon"
                    className="dt-input"
                    value={form.horizon}
                    onChange={(e) => updateField('horizon', e.target.value)}
                  >
                    {horizons.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </select>
                  {!advanced && (<div className="dt-help">Basic plan: Daily / Two-Day / Weekly only.</div>)}
                </div>

	                <div className="dt-field">
	                  <label htmlFor="tradeFrequency">
                      Trade Frequency (business days) <HelpTip id="help-tradeFrequency" text={FIELD_HELP.tradeFrequency} />
                    </label>
	                  <input
	                    id="tradeFrequency"
	                    type="number"
	                    min={1}
                    step={1}
                    className="dt-input"
                    value={form.tradeFrequencyBdays}
                    onChange={(e) => updateField('tradeFrequencyBdays', clampNumber(e.target.value, 1, 252))}
                  />
                  <div className="dt-help">Minimum business days between decisions.</div>
                </div>

		                <div className="dt-field">
		                  <label htmlFor="fromDate">
		                    From Date <span className="dt-required">*</span>
	                      <HelpTip id="help-fromDate" text={FIELD_HELP.fromDate} />
		                  </label>
                      <SingleDatePicker
                        value={form.fromDate}
                        onChange={(val) => updateField('fromDate', val)}
                        placeholder="Select date"
                        error={Boolean(dateError)}
                      />
	                </div>
	
		                <div className="dt-field">
		                  <label htmlFor="toDate">
		                    To Date <span className="dt-required">*</span>
	                      <HelpTip id="help-toDate" text={FIELD_HELP.toDate} />
		                  </label>
                      <SingleDatePicker
                        value={form.toDate}
                        onChange={(val) => updateField('toDate', val)}
                        placeholder="Select date"
                        error={Boolean(dateError)}
                      />
	                  {dateError ? <div className="dt-field-error">{dateError}</div> : null}
	                </div>

	                <div className="dt-field">
	                  <label htmlFor="initialCapital">
                      Initial Capital <HelpTip id="help-initialCapital" text={FIELD_HELP.initialCapital} />
                    </label>
	                  <input
	                    id="initialCapital"
	                    type="text"
                      inputMode="decimal"
                      aria-invalid={Boolean(numericValidation.errors.initialCapital)}
                      aria-describedby={numericValidation.errors.initialCapital ? 'initialCapital-error' : undefined}
                    className="dt-input"
	                    value={form.initialCapital}
	                    onChange={(e) => updateField('initialCapital', e.target.value)}
                  />
                  {numericValidation.errors.initialCapital ? <div id="initialCapital-error" className="dt-field-error" role="alert">{numericValidation.errors.initialCapital}</div> : null}
                </div>

                {usingRuleStrategy ? (
                  <div className="dt-field dt-field-full dt-rule-builder">
                    <div className="dt-rule-builder-title">Custom Rule</div>
                    <div className="dt-rule-builder-grid">
                      <div className="dt-rule-field">
                        <label htmlFor="ruleLabel">Rule Label</label>
                        <input
                          id="ruleLabel"
                          className="dt-input"
                          value={selectedRule.label || ''}
                          onChange={(e) => updatePrimaryRule({ label: e.target.value })}
                          placeholder="Rule label"
                        />
                      </div>

                      <div className="dt-rule-field">
                        <label htmlFor="ruleField">
                          Field <HelpTip id="help-ruleField" text={FIELD_HELP.ruleField} />
                        </label>
                        <select
                          id="ruleField"
                          className="dt-input"
                          value={selectedRuleCondition.field || selectedRuleField?.id || ''}
                          onChange={(e) => updateRuleField(e.target.value)}
                        >
                          {ruleFieldsForMode.map((field) => (
                            <option key={field.id} value={field.id}>
                              {field.category ? `${field.category} - ` : ''}{field.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="dt-rule-field">
                        <label htmlFor="ruleOperator">
                          Operator <HelpTip id="help-ruleOperator" text={FIELD_HELP.ruleOperator} />
                        </label>
                        <select
                          id="ruleOperator"
                          className="dt-input"
                          value={selectedRuleCondition.operator || ''}
                          onChange={(e) => updateRuleOperator(e.target.value)}
                        >
                          {ruleOperatorsForField.map((operator) => (
                            <option key={operator.value} value={operator.value}>
                              {operator.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="dt-rule-field">
                        <label htmlFor="ruleValue">
                          Value <HelpTip id="help-ruleValue" text={FIELD_HELP.ruleValue} />
                        </label>
                        {ruleValueIsHidden ? (
                          <input
                            id="ruleValue"
                            className="dt-input"
                            value="No value required"
                            disabled
                            readOnly
                          />
                        ) : selectedRuleCondition.operator === 'between' ? (
                          <div className="dt-rule-between">
                            <input
                              id="ruleValue"
                              className="dt-input"
                              type={selectedRuleField?.type === 'date' ? 'date' : 'number'}
                              value={selectedBetweenValue[0] ?? ''}
                              onChange={(e) => updatePrimaryCondition({ value: [e.target.value, selectedBetweenValue[1] ?? ''] })}
                              placeholder={selectedRuleFieldIsPercent ? 'Min %' : 'Min'}
                            />
                            <input
                              className="dt-input"
                              type={selectedRuleField?.type === 'date' ? 'date' : 'number'}
                              value={selectedBetweenValue[1] ?? ''}
                              onChange={(e) => updatePrimaryCondition({ value: [selectedBetweenValue[0] ?? '', e.target.value] })}
                              placeholder={selectedRuleFieldIsPercent ? 'Max %' : 'Max'}
                            />
                          </div>
                        ) : selectedRuleField?.type === 'text' ? (
                          <select
                            id="ruleValue"
                            className="dt-input"
                            value={selectedRuleValueText}
                            onChange={(e) => updatePrimaryCondition({ value: e.target.value })}
                          >
                            {sentimentRuleValueOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id="ruleValue"
                            className="dt-input"
                            type={selectedRuleField?.type === 'date' ? 'date' : 'number'}
                            step={selectedRuleField?.type === 'date' ? undefined : ruleNumberStep}
                            value={selectedRuleValueText}
                            onChange={(e) => updatePrimaryCondition({ value: e.target.value })}
                            placeholder={ruleValuePlaceholder}
                          />
                        )}
                      </div>

                      <div className="dt-rule-field">
                        <label htmlFor="ruleAction">
                          Match Action <HelpTip id="help-ruleAction" text={FIELD_HELP.ruleAction} />
                        </label>
                        <select
                          id="ruleAction"
                          className="dt-input"
                          value={selectedRule.action || 'buy'}
                          onChange={(e) => updatePrimaryRule({ action: e.target.value })}
                        >
                          {strategyActions.map((action) => (
                            <option key={action.value} value={action.value}>{action.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="dt-rule-field">
                        <label htmlFor="ruleDefaultAction">
                          Else Action <HelpTip id="help-ruleDefaultAction" text={FIELD_HELP.ruleDefaultAction} />
                        </label>
                        <select
                          id="ruleDefaultAction"
                          className="dt-input"
                          value={selectedRuleDefinition.default_action || 'hold'}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateRuleDefinition((definition) => {
                              definition.default_action = value;
                            });
                          }}
                        >
                          {strategyActions.map((action) => (
                            <option key={action.value} value={action.value}>{action.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ) : null}

                {usingPredictionSettings ? (
                  <>
	                <div className="dt-field">
	                  <label htmlFor="upAction">
                      If Predicted Return ≥ 0 <HelpTip id="help-upAction" text={FIELD_HELP.upAction} />
                    </label>
	                  <select
	                    id="upAction"
	                    className="dt-input"
	                    value={form.upAction}
                    onChange={(e) => updateField('upAction', e.target.value)}
                  >
                    {actions.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>

	                <div className="dt-field">
	                  <label htmlFor="downAction">
                      If Predicted Return &lt; 0 <HelpTip id="help-downAction" text={FIELD_HELP.downAction} />
                    </label>
	                  <select
	                    id="downAction"
	                    className="dt-input"
	                    value={form.downAction}
                    onChange={(e) => updateField('downAction', e.target.value)}
                  >
                    {actions.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dt-field">
                  <label htmlFor="sentimentCondition">
                    Market Sentiment
                    <HelpTip id="help-sentimentCondition" text={FIELD_HELP.sentimentCondition} />
                  </label>
                  <select
                    id="sentimentCondition"
                    className="dt-input"
                    value={form.sentimentCondition}
                    onChange={(e) => updateField('sentimentCondition', e.target.value)}
                  >
                    {sentimentConditions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dt-field">
                  <label htmlFor="missingSentimentBehavior">
                    If Sentiment Missing
                    <HelpTip id="help-missingSentimentBehavior" text={FIELD_HELP.missingSentimentBehavior} />
                  </label>
                  <select
                    id="missingSentimentBehavior"
                    className="dt-input"
                    value={form.missingSentimentBehavior}
                    onChange={(e) => updateField('missingSentimentBehavior', e.target.value)}
                  >
                    {missingSentimentBehaviors.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <div className="dt-help">Default: Treat Missing As Neutral.</div>
                </div>

	                <div className="dt-field">
		                  <label htmlFor="minPredictedReturn">
                      Minimum Predicted Return <HelpTip id="help-minPredictedReturn" text={FIELD_HELP.minPredictedReturn} />
                    </label>
	                  <input
	                    id="minPredictedReturn"
	                    type="text"
                      inputMode="decimal"
                      aria-invalid={Boolean(numericValidation.errors.minPredictedReturn)}
                      aria-describedby={numericValidation.errors.minPredictedReturn ? 'minPredictedReturn-error' : undefined}
                    className="dt-input"
                    value={form.minPredictedReturn}
                    onChange={(e) => updateField('minPredictedReturn', e.target.value)}
                    placeholder="e.g., -0.10"
                  />
                  {numericValidation.errors.minPredictedReturn ? <div id="minPredictedReturn-error" className="dt-field-error" role="alert">{numericValidation.errors.minPredictedReturn}</div> : null}
                  <div className="dt-help">Optional filter (return units). Example: -0.10 means -10%.</div>
                </div>

	                <div className="dt-field">
	                  <label htmlFor="maxPredictedReturn">
                      Maximum Predicted Return <HelpTip id="help-maxPredictedReturn" text={FIELD_HELP.maxPredictedReturn} />
                    </label>
	                  <input
	                    id="maxPredictedReturn"
	                    type="text"
                      inputMode="decimal"
                      aria-invalid={Boolean(numericValidation.errors.maxPredictedReturn)}
                      aria-describedby={numericValidation.errors.maxPredictedReturn ? 'maxPredictedReturn-error' : undefined}
                    className="dt-input"
                    value={form.maxPredictedReturn}
                    onChange={(e) => updateField('maxPredictedReturn', e.target.value)}
                    placeholder="e.g., 0.02"
	                  />
                  {numericValidation.errors.maxPredictedReturn ? <div id="maxPredictedReturn-error" className="dt-field-error" role="alert">{numericValidation.errors.maxPredictedReturn}</div> : null}
	                  <div className="dt-help">Optional filter (return units). Example: 0.02 means +2%.</div>
	                </div>
                  </>
                ) : null}

                {optionsStrategiesEnabled && form.instrumentType === 'option' ? (
                  <>
                    <div className="dt-field dt-field-full dt-options-section-title">
                      <div>
                        <span>Options Settings</span>
                        <small>Simulates BUY/SELL CALL or BUY/SELL PUT actions from DeepTrading stock signals. DeepTrading.ai does not place trades.</small>
                      </div>
                    </div>

                    <div className="dt-field dt-field-full dt-options-risk-note">
                      Options involve significant risk and can expire worthless. This panel is an educational simulation only and models long-options actions, not option writing.
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionTemplateId">
                        Options Template <HelpTip id="help-optionTemplate" text={FIELD_HELP.optionTemplate} />
                      </label>
                      <select
                        id="optionTemplateId"
                        className="dt-input"
                        value={form.optionTemplateId}
                        onChange={(e) => updateField('optionTemplateId', e.target.value)}
                      >
                        {optionTemplatePresets.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      {selectedOptionTemplatePreset?.description ? (
                        <div className="dt-help">{selectedOptionTemplatePreset.description}</div>
                      ) : null}
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionType">
                        Contract Side <HelpTip id="help-optionType" text={FIELD_HELP.optionType} />
                      </label>
                      <select
                        id="optionType"
                        className="dt-input"
                        value={form.optionType}
                        onChange={(e) => updateField('optionType', e.target.value)}
                      >
                        {optionTypes.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionActionMode">
                        Option Trigger <HelpTip id="help-optionActionMode" text={FIELD_HELP.optionActionMode} />
                      </label>
                      <select
                        id="optionActionMode"
                        className="dt-input"
                        value={form.optionActionMode}
                        onChange={(e) => updateField('optionActionMode', e.target.value)}
                      >
                        {availableOptionActionModes.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionPriceModel">
                        Fill Model <HelpTip id="help-optionPriceModel" text={FIELD_HELP.optionPriceModel} />
                      </label>
                      <select
                        id="optionPriceModel"
                        className="dt-input"
                        value={form.optionPriceModel}
                        onChange={(e) => updateField('optionPriceModel', e.target.value)}
                      >
                        {optionPriceModels.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionMaxSpreadPct">
                        Max Spread % <HelpTip id="help-optionMaxSpreadPct" text={FIELD_HELP.optionMaxSpreadPct} />
                      </label>
                      <input
                        id="optionMaxSpreadPct"
                        type="number"
                        min={0}
                        step={1}
                        className="dt-input"
                        value={form.optionMaxSpreadPct}
                        onChange={(e) => updateField('optionMaxSpreadPct', e.target.value)}
                        placeholder="e.g., 35"
                      />
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionMinVolume">
                        Min Volume <HelpTip id="help-optionMinVolume" text={FIELD_HELP.optionMinVolume} />
                      </label>
                      <input
                        id="optionMinVolume"
                        type="number"
                        min={0}
                        step={1}
                        className="dt-input"
                        value={form.optionMinVolume}
                        onChange={(e) => updateField('optionMinVolume', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="dt-field">
                      <label htmlFor="optionMinOpenInterest">
                        Min Open Interest <HelpTip id="help-optionMinOpenInterest" text={FIELD_HELP.optionMinOpenInterest} />
                      </label>
                      <input
                        id="optionMinOpenInterest"
                        type="number"
                        min={0}
                        step={1}
                        className="dt-input"
                        value={form.optionMinOpenInterest}
                        onChange={(e) => updateField('optionMinOpenInterest', e.target.value)}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="dt-field dt-field-full">
                      <div className="dt-options-contract-header">
                        <label htmlFor="optionSymbol">
                          Historical Contract <span className="dt-required">*</span>
                          <HelpTip id="help-optionContract" text={FIELD_HELP.optionContract} />
                        </label>
                        <div className="dt-options-contract-actions">
                          <button
                            type="button"
                            className="dt-btn dt-btn-primary dt-action-btn"
                            onClick={onAutoSelectOptionContract}
                            disabled={!canAutoSelectOptionContract}
                            title={autoSelectOptionContractTitle}
                          >
                            {optionsSelectContractLoading ? 'Selecting…' : 'Auto-select Contract'}
                          </button>
                          <button
                            type="button"
                            className="dt-btn dt-btn-primary dt-action-btn"
                            onClick={onLoadOptionContracts}
                            disabled={!form.ticker || !form.fromDate || !form.toDate || optionContractsLoading}
                          >
                            {optionContractsLoading ? 'Loading…' : 'Load Contracts'}
                          </button>
                        </div>
                      </div>

                      <label htmlFor="autoReselectOptionContract" className="dt-checkbox-row dt-option-auto-contract">
                        <input
                          id="autoReselectOptionContract"
                          type="checkbox"
                          className="dt-checkbox"
                          checked={Boolean(form.autoReselectOptionContract)}
                          onChange={(e) => updateField('autoReselectOptionContract', e.target.checked)}
                        />
                        <span>Re-select on each backtest run</span>
                        <HelpTip
                          id="help-optionAutoReselectContract"
                          text={FIELD_HELP.optionAutoReselectContract}
                        />
                      </label>

                      <select
                        id="optionSymbol"
                        className="dt-input"
                        value={form.optionSymbol}
                        onChange={(e) => updateField('optionSymbol', e.target.value)}
                      >
                        <option value="">Select available contract…</option>
                        {optionContracts.map((contract) => (
                          <option key={contract.option_symbol} value={contract.option_symbol}>
                            {contract.option_symbol} | {String(contract.contract_type || '').toUpperCase()} | ${Number(contract.strike || 0).toFixed(2)} | Exp {contract.expiration_date}
                          </option>
                        ))}
                      </select>
                      {optionAutoSelectionStale ? (
                        <div className="dt-options-stale-note">
	                          Strategy settings changed. The selected auto contract may no longer be optimal.
	                          <button
	                            type="button"
	                            className="dt-inline-link-btn"
	                            onClick={onAutoSelectOptionContract}
	                            disabled={!canAutoSelectOptionContract}
	                            title={autoSelectOptionContractTitle}
	                          >
	                            Re-select Contract
	                          </button>
                        </div>
                      ) : null}
                      {optionContractsMessage ? <div className="dt-help">{optionContractsMessage}</div> : null}

                      {optionContracts.length ? (
                        <div className="dt-options-contract-list">
                          {optionContracts.slice(0, 30).map((contract) => (
                            <button
                              type="button"
                              key={contract.option_symbol}
                              className={`dt-options-contract-row ${form.optionSymbol === contract.option_symbol ? 'dt-options-contract-row--selected' : ''}`}
                              onClick={() => updateField('optionSymbol', contract.option_symbol)}
                            >
                              <span>{contract.option_symbol}</span>
                              <span>{String(contract.contract_type || '').toUpperCase()}</span>
                              <span>${Number(contract.strike || 0).toFixed(2)}</span>
                              <span>Exp {contract.expiration_date}</span>
                              <span>{contract.historical_rows} rows</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </div>

              <footer className="dt-card-footer">
                <div className="dt-card-footer-main">
                  {dirty ? (
                    <span className="dt-dirty">● Unsaved changes</span>
                  ) : form.savedStrategyName ? (
                    <span className="dt-clean">✓ Saved</span>
                  ) : (
                    <span className="dt-muted">Not saved yet</span>
                  )}
                  <span className="dt-muted">
                    {strategiesLoading ? 'Loading saved strategies…' : ''}
                  </span>
                  {activationIssues.length ? (
                    <span className="dt-strategy-activation-warning">
                      Draft only: add {activationIssueText} before it can be Active.
                    </span>
                  ) : null}
                  {customStrategySaveUnavailable ? (
                    <span className="dt-strategy-activation-warning">
                      Custom rule saves need the database migration. Run Backtest is available.
                    </span>
                  ) : null}
                  {strategyNotice ? (
                    <span className="dt-strategy-save-notice">{strategyNotice}</span>
                  ) : null}
                  {!usingPredictionSettings && (numericValidation.errors.minPredictedReturn || numericValidation.errors.maxPredictedReturn) ? (
                    <span className="dt-field-error" role="alert">
                      {numericValidation.errors.minPredictedReturn || numericValidation.errors.maxPredictedReturn} Edit these bounds in DeepTrading Prediction mode.
                    </span>
                  ) : null}
                </div>

                {strategyMarketplaceEnabled ? (
                  <div className="dt-card-footer-marketplace" aria-label="Strategy marketplace options">
                    <span className="dt-card-footer-marketplace-label">Marketplace</span>

                    <label htmlFor="makePublicStrategy" className="dt-marketplace-inline-option dt-marketplace-inline-option--primary">
                      <input
                        id="makePublicStrategy"
                        type="checkbox"
                        className="dt-checkbox"
                        checked={Boolean(form.makePublic)}
                        onChange={(e) => updateField('makePublic', e.target.checked)}
                      />
                      <span>Public</span>
                      <HelpTip id="help-marketplacePublic" text={FIELD_HELP.marketplacePublic} />
                    </label>

                    <label htmlFor="showParametersPublicly" className={`dt-marketplace-inline-option ${!Boolean(form.makePublic) ? 'dt-marketplace-inline-option--disabled' : ''}`}>
                      <input
                        id="showParametersPublicly"
                        type="checkbox"
                        className="dt-checkbox"
                        checked={Boolean(form.showParametersPublicly)}
                        onChange={(e) => updateField('showParametersPublicly', e.target.checked)}
                        disabled={!Boolean(form.makePublic)}
                      />
                      <span>Show Parameters</span>
                      <HelpTip id="help-marketplaceShowParameters" text={FIELD_HELP.marketplaceShowParameters} />
                    </label>

                    <label htmlFor="allowCloneStrategy" className={`dt-marketplace-inline-option ${!Boolean(form.makePublic) ? 'dt-marketplace-inline-option--disabled' : ''}`}>
                      <input
                        id="allowCloneStrategy"
                        type="checkbox"
                        className="dt-checkbox"
                        checked={Boolean(form.allowClone)}
                        onChange={(e) => updateField('allowClone', e.target.checked)}
                        disabled={!Boolean(form.makePublic)}
                      />
                      <span>Allow Clone</span>
                      <HelpTip id="help-marketplaceAllowClone" text={FIELD_HELP.marketplaceAllowClone} />
                    </label>
                  </div>
                ) : null}
              </footer>
            </section>

            <div
              className="dt-results-stack"
              ref={resultsStackRef}
              style={{ width: `${resultPanelsWidthPercent}%` }}
            >
              <section className="dt-card dt-card-pad dt-card-summary">
                <header className="dt-card-header dt-card-header-tight">
                  <div>
                    <h2 className="dt-card-title">Backtest Summary</h2>
                  </div>

                  <div className="dt-card-header-actions">
                    <button
                      type="button"
                      className="dt-btn dt-btn-primary dt-action-btn"
                      onClick={onExportCsv}
                      disabled={!rows.length || backtestBusy}
                      title={!rows.length ? 'Run a backtest first' : 'Export backtest trades to CSV'}
                    >
                      Export to CSV
                    </button>

                    <button
                      type="button"
                      className="dt-btn dt-btn-accent dt-action-btn"
                      onClick={onRunBacktest}
                      disabled={!canRun || backtestBusy}
                      title={!canRun ? canRunBlockReason : 'Generate backtest'}
                    >
                      {backtestBusy ? 'Running…' : 'Generate Backtest'}
                    </button>
                  </div>
	                </header>
                {backtestError ? (
                  <div className="dt-backtest-error">
                    {backtestError}
                  </div>
                ) : null}
                {comparison.length ? (
                  <div className="dt-comparison">
                    {comparison.map((item) => (
                      <div key={item.label} className="dt-comparison-card">
                        <div className="dt-comparison-label">{item.label}</div>
                        <div className="dt-comparison-value">
                          {item.total_return_pct == null ? '—' : formatPct(Number(item.total_return_pct))}
                        </div>
                        <div className="dt-comparison-meta">
                          Final equity: {item.final_equity == null ? '—' : formatMoney(Number(item.final_equity))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
		                {isOptionsResult && marketBenchmark?.total_return_pct != null ? (
		                  <div className="dt-market-benchmark-line">
                    <div className="dt-market-benchmark-left">
                      <span className="dt-market-benchmark-label">
                        Market benchmark
                        <HelpTip id="help-marketBenchmark" text={FIELD_HELP.marketBenchmark} className="dt-help-icon--small" />
                      </span>
                      <span className="dt-market-benchmark-name">
                        {marketBenchmark.label || marketBenchmark.ticker || 'Market'}: {formatPct(Number(marketBenchmark.total_return_pct))}
                      </span>
                    </div>
                    <div className="dt-market-benchmark-right">
                      <span>{isOptionsResult ? 'Options vs market' : 'Strategy vs market'}</span>
                      <strong>{formatPctPoints(marketBenchmark.strategy_vs_benchmark_pct)}</strong>
		                    </div>
		                  </div>
		                ) : null}
		                {!isOptionsResult && stockAttribution ? (
		                  <div className="dt-options-insight dt-stock-insight">
		                    <div className="dt-options-insight-main">
		                      <div>
		                        <div className="dt-options-insight-label">Stock Readout</div>
		                        <div className="dt-options-insight-headline">{stockAttribution.headline}</div>
		                      </div>
		                      <div className="dt-options-insight-metrics">
		                        <span>Strategy vs Buy & Hold: {formatPctPoints(stockAttribution.metrics?.strategy_vs_buy_hold_pct)}</span>
		                        <span>Strategy vs market: {formatPctPoints(stockAttribution.metrics?.strategy_vs_benchmark_pct)}</span>
		                        <span>Win rate: {stockAttribution.metrics?.win_rate_pct == null ? '—' : formatPlainPct(Number(stockAttribution.metrics.win_rate_pct))}</span>
		                      </div>
		                    </div>
		                    <div className="dt-options-insight-columns">
		                      <div>
		                        <div className="dt-options-insight-subtitle">Why it happened</div>
		                        <ul>
		                          {(stockAttribution.reasons || []).map((reason) => (
		                            <li key={reason}>{reason}</li>
		                          ))}
		                        </ul>
		                      </div>
		                      <div>
		                        <div className="dt-options-insight-subtitle">What to try</div>
		                        <ul>
		                          {(stockAttribution.suggestions || []).map((suggestion) => (
		                            <li key={suggestion}>{suggestion}</li>
		                          ))}
		                        </ul>
		                      </div>
		                    </div>
		                  </div>
		                ) : null}
		                {!isOptionsResult && stockTemplateResults.length ? (
		                  <div className="dt-options-templates dt-stock-templates">
		                    <div className="dt-options-templates-header">
		                      <div>
		                        <div className="dt-options-insight-label">Stock Templates</div>
		                        <div className="dt-options-templates-title">Fixed strategy comparison for the same ticker, horizon, and date range</div>
		                      </div>
		                    </div>
		                    <div className="dt-options-template-grid">
		                      {stockTemplateResults.map((template) => (
		                        <div
		                          key={template.template_id || template.label}
		                          className={`dt-options-template-card ${form.stockTemplateId === template.template_id ? 'dt-options-template-card--active' : ''}`}
		                        >
		                          <div className="dt-options-template-top">
		                            <span>{template.label}</span>
		                            <strong>{template.total_return_pct == null ? '—' : formatPct(Number(template.total_return_pct))}</strong>
		                          </div>
		                          <div className="dt-stock-template-settings">
		                            {Number(template.n_trades || 0)} trades
		                            {' | '}
		                            {template.win_rate == null ? 'win —' : `win ${(Number(template.win_rate) * 100).toFixed(1)}%`}
		                          </div>
		                          <div className="dt-options-template-meta">
		                            <span>vs Buy & Hold {formatPctPoints(template.vs_buy_hold_pct)}</span>
		                            <span>vs market {formatPctPoints(template.vs_benchmark_pct)}</span>
		                          </div>
		                          <div className="dt-options-template-description">{template.description}</div>
		                        </div>
		                      ))}
		                    </div>
		                  </div>
		                ) : null}
		                {isOptionsResult && optionsAttribution ? (
	                  <div className="dt-options-insight">
	                    <div className="dt-options-insight-main">
	                      <div>
	                        <div className="dt-options-insight-label">Options Readout</div>
	                        <div className="dt-options-insight-headline">{optionsAttribution.headline}</div>
	                      </div>
	                      <div className="dt-options-insight-metrics">
	                        <span>Options vs stock: {formatPctPoints(optionsAttribution.metrics?.options_vs_stock_pct)}</span>
	                        <span>Execution rate: {optionsAttribution.metrics?.execution_rate_pct == null ? '—' : formatPlainPct(Number(optionsAttribution.metrics.execution_rate_pct))}</span>
	                        <span>Avg spread: {optionsAttribution.metrics?.average_entry_spread_pct == null ? '—' : formatPlainPct(Number(optionsAttribution.metrics.average_entry_spread_pct))}</span>
	                      </div>
	                    </div>
	                    <div className="dt-options-insight-columns">
	                      <div>
	                        <div className="dt-options-insight-subtitle">Why it happened</div>
	                        <ul>
	                          {(optionsAttribution.reasons || []).map((reason) => (
	                            <li key={reason}>{reason}</li>
	                          ))}
	                        </ul>
	                      </div>
	                      <div>
	                        <div className="dt-options-insight-subtitle">What to try</div>
	                        <ul>
	                          {(optionsAttribution.suggestions || []).map((suggestion) => (
	                            <li key={suggestion}>{suggestion}</li>
	                          ))}
	                        </ul>
	                      </div>
	                    </div>
	                  </div>
	                ) : null}
	                {isOptionsResult && optionTemplateResults.length ? (
	                  <div className="dt-options-templates">
	                    <div className="dt-options-templates-header">
	                      <div>
	                        <div className="dt-options-insight-label">Options Templates</div>
	                        <div className="dt-options-templates-title">Fixed playbook comparison for the same stock signal</div>
	                      </div>
	                    </div>
	                    <div className="dt-options-template-grid">
	                      {optionTemplateResults.map((template) => (
	                        <div
	                          key={template.template_id || template.label}
	                          className={`dt-options-template-card ${form.optionTemplateId === template.template_id ? 'dt-options-template-card--active' : ''}`}
	                        >
	                          <div className="dt-options-template-top">
	                            <span>{template.label}</span>
	                            <strong>{template.total_return_pct == null ? '—' : formatPct(Number(template.total_return_pct))}</strong>
	                          </div>
	                          <div className="dt-options-template-contract">
	                            {formatContractSummary(template.selected_contract) || 'No executable contract'}
	                          </div>
	                          <div className="dt-options-template-meta">
	                            <span>{Number(template.executed_trades || 0)} executed</span>
	                            <span>{Number(template.skipped_trades || 0)} skipped</span>
	                            <span>{template.average_entry_spread_pct == null ? 'spread —' : `spread ${formatPlainPct(Number(template.average_entry_spread_pct))}`}</span>
	                          </div>
	                          <div className="dt-options-template-description">{template.description}</div>
	                        </div>
	                      ))}
	                    </div>
	                  </div>
	                ) : null}
	                {isOptionsResult ? (
	                  <div className="dt-options-diagnostics">
                    <div className="dt-options-diagnostics-main">
                      <div>
                        <span className="dt-options-diagnostics-label">
                          {optionSelection?.mode === 'auto'
                            ? (optionSelection?.selected === false ? 'Closest evaluated contract' : 'Auto-selected contract')
                            : 'Selected contract'}
                        </span>
                        <span className="dt-options-diagnostics-value">
                          {optionSelectedContractText || result?.option_strategy?.option_symbol || '—'}
                        </span>
                      </div>
                      <div className="dt-options-diagnostics-stats">
                        <span>{optionExecutedCount} executed</span>
                        <span>{Math.max(0, optionSignalCount - optionExecutedCount)} not executed</span>
                        <span>{optionSignalCount} option signal rows</span>
                      </div>
                    </div>
                    {optionSelection?.reason ? (
                      <div className="dt-options-diagnostics-detail">{optionSelection.reason}</div>
                    ) : null}
                    {optionSkipReasonEntries.length ? (
                      <div className="dt-options-skip-reasons">
                        {optionSkipReasonEntries.map(([reason, count]) => (
                          <span key={reason} className="dt-options-skip-chip">
                            {formatSkipReason(reason)}: {count}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="dt-kpis">
                  <div className="dt-kpi">
                    <div className="dt-kpi-label">
                      Trades <HelpTip id="help-backtest-trades" text={FIELD_HELP.backtestTrades} className="dt-help-icon--small" />
                    </div>
                    <div className="dt-kpi-value">
                      {summary ? Number(summary.n_executed_trades ?? ((summary.n_buy || 0) + (summary.n_short || 0))) : '—'}
                    </div>
                  </div>
                  <div className="dt-kpi">
                    <div className="dt-kpi-label">
                      Win rate <HelpTip id="help-backtest-winrate" text={FIELD_HELP.backtestWinRate} className="dt-help-icon--small" />
                    </div>
                    <div className="dt-kpi-value">
                      {summary?.win_rate == null ? '—' : `${(summary.win_rate * 100).toFixed(1)}%`}
                    </div>
                  </div>
                  <div className="dt-kpi dt-kpi-split">
                    <div className="dt-kpi-split-col">
                      <div className="dt-kpi-label">
                        Total return <HelpTip id="help-backtest-total" text={FIELD_HELP.backtestTotalReturn} className="dt-help-icon--small" />
                      </div>
                      <div className="dt-kpi-value">
                        {summary ? formatPct(summary.total_return_pct) : '—'}
                      </div>
                    </div>

                    <div className="dt-kpi-split-divider" />

                    <div className="dt-kpi-split-col">
                      <div className="dt-kpi-label">
                        {isOptionsResult ? 'Skipped signals' : 'Buy & hold return'}
                        <HelpTip
                          id={isOptionsResult ? 'help-backtest-skipped-options' : 'help-backtest-buyhold'}
                          text={isOptionsResult ? FIELD_HELP.backtestSkippedOptionSignals : FIELD_HELP.backtestBuyHoldReturn}
                          className="dt-help-icon--small"
                        />
                      </div>
                      <div className="dt-kpi-value">
                        {isOptionsResult
                          ? `${Number(summary?.n_skipped || 0)} / ${Number(summary?.n_option_signals || 0)}`
                          : (buyHoldReturnPct == null ? '—' : formatPct(buyHoldReturnPct))}
                      </div>
                    </div>
                  </div>
                  <div className="dt-kpi">
                    <div className="dt-kpi-label">
                      Final equity <HelpTip id="help-backtest-finalequity" text={FIELD_HELP.backtestFinalEquity} className="dt-help-icon--small" />
                    </div>
                    <div className="dt-kpi-value">
                      {summary ? formatMoney(summary.final_equity) : '—'}
                    </div>
                  </div>
                </div>

                <div className="dt-summary-note">
                  {isOptionsResult
                    ? 'Options metrics use the selected historical contract with conservative entry/exit pricing. A 0% result usually means signals were skipped because prices were missing, filters rejected the row, or one contract cost more than available capital.'
                    : 'These metrics come from the simulated trades (Buy/Sell/Short/No Trade). Sell is flat; Short is inverse exposure. Adjust the horizon, frequency, and filters to explore what works best.'}
                </div>
              </section>

              <section
                className="dt-card dt-card-pad dt-card-results dt-card-results-resizable"
                ref={tradesCardRef}
                style={{ height: `${tablePanelHeight}px` }}
              >
                <header className="dt-results-head">
                  <div className="dt-results-title">
                    <h2 className="dt-card-title">Trades</h2>
                    <HelpModalIcon
                      ariaLabel="Trades table column guide"
                      onClick={() => setShowTradesHelp(true)}
                    />
                  </div>
                  <div className="dt-results-meta">
                    {backtestBusy ? 'Running backtest…' : (rows.length ? `${rows.length} rows` : 'No results yet')}
                  </div>
                </header>
                <div className="dt-results-tip-row">
                  <div className="dt-results-tip">
                    Tip: drag the panel bottom edge, right edge, or corner grip to resize.
                  </div>
                  <button
                    type="button"
                    className="dt-panel-reset-btn dt-results-reset-btn"
                    onClick={resetPanelSize}
                    title="Reset panel width and height to default"
                  >
                    Reset Panel Size
                  </button>
                </div>
                <div className="dt-strategies-table-panel">
                  <div className="dt-strategies-table-grid">
                    <Grid
                      preference={isOptionsResult ? STRATEGIES_OPTIONS_BACKTEST_TABLE_PREFERENCE : STRATEGIES_BACKTEST_TABLE_PREFERENCE}
                      dataSource={rows}
                      loading={backtestBusy}
                      className="selectStockModal dt-strategies-grid"
                      columns={columns}
                      idProperty="id"
                      rowHeight={35}
                      sortable
                      defaultSortInfo={defaultSortInfo}
                      enableFiltering
                      defaultFilterValue={defaultFilterValue}
                      enableColumnFilterContextMenu={false}
                      showColumnMenuTool
                      showColumnMenuSortOptions={false}
                      showColumnMenuGroupOptions={false}
                      showColumnMenuFilterOptions={false}
                      showColumnMenuLockOptions={false}
                      headerHeight={48}
                      style={gridStyle}
                      scrollProps={{ autoHide: true }}
                    />
                  </div>
                  <div
                    className="dt-strategies-table-resizer-y"
                    onMouseDown={startTableResizeY}
                    title="Drag vertically to resize panel"
                  />
                  <div
                    className="dt-strategies-table-resizer-x"
                    onMouseDown={startTableResizeX}
                    title="Drag horizontally to resize panel"
                  />
                  <div
                    className="dt-strategies-table-corner-resizer"
                    onMouseDown={startTableResizeBoth}
                    title="Drag to resize panel"
                  />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: '#231919' }}>
        <Modal
          show={showOptionsRiskModal}
          className="strategyHelpModal optionsRiskModal"
          size="lg"
          centered
          backdrop="static"
          keyboard={false}
          onHide={cancelOptionsRiskAcknowledgement}
        >
          <Modal.Body className="tsHelpModalBody">
            <div className="tsHelp optionsRisk">
              <div className="tsHelp__header optionsRisk__header">
                <div className="tsHelp__headerText">
                  <div className="tsHelp__title optionsRisk__title">Options Trading Risk Acknowledgement</div>
                </div>
              </div>

              <div className="tsHelp__content optionsRisk__content">
                <div className="optionsRisk__badge">Important</div>
                <p className="tsHelp__p tsHelp__intro optionsRisk__intro">
                  {optionsRiskDisclaimer}
                </p>

                <div className="optionsRisk__terms">
                  <div><b>BUY CALL</b> and <b>BUY PUT</b> open long option simulations.</div>
                  <div><b>SELL CALL</b> and <b>SELL PUT</b> close existing long option simulations.</div>
                  <div>DeepTrading.ai does not model opening short option-writing positions in this release.</div>
                </div>

                <label htmlFor="optionsRiskAcknowledged" className="dt-checkbox-row optionsRisk__check">
                  <input
                    id="optionsRiskAcknowledged"
                    type="checkbox"
                    className="dt-checkbox"
                    checked={Boolean(optionsRiskChecked)}
                    onChange={(e) => setOptionsRiskChecked(e.target.checked)}
                  />
                  <span>
                    I understand the risks of options trading and that DeepTrading.ai provides educational simulations only.
                  </span>
                </label>

                <div className="optionsRisk__renewal">
                  This acknowledgement is stored with your account and expires after {optionsRiskExpiresDays} days, or sooner if the disclaimer changes.
                </div>

                <div className="tsHelp__actions optionsRisk__actions">
                  <button
                    type="button"
                    className="dt-btn dt-btn-secondary dt-action-btn"
                    onClick={cancelOptionsRiskAcknowledgement}
                    disabled={optionsRiskAcceptLoading}
                  >
                    Back to Stocks
                  </button>
                  <button
                    type="button"
                    className="dt-btn dt-btn-accent dt-action-btn"
                    onClick={confirmOptionsRiskAcknowledgement}
                    disabled={!optionsRiskChecked || optionsRiskAcceptLoading || optionsRiskLoading}
                  >
                    {optionsRiskAcceptLoading ? 'Saving…' : 'Continue to Options'}
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={showHelp}
          className="strategyHelpModal"
          size="lg"
          centered
          onHide={() => setShowHelp(false)}
        >
          <Modal.Body className="tsHelpModalBody">
            <div className="tsHelp">
              <div className="tsHelp__header">
                <div className="tsHelp__headerText">
                  <div className="tsHelp__title">{STRATEGY_HELP_TITLE}</div>
                </div>

                <button
                  type="button"
                  className="tsHelp__closeIcon"
                  aria-label="Close"
                  onClick={() => setShowHelp(false)}
                >
                  ×
                </button>
              </div>

              <div className="tsHelp__content">
                <p className="tsHelp__p tsHelp__intro">
                  Trading Strategies lets you backtest a simple rules-based approach using DeepTrading.ai predictions as your guide.
                  It does <b>not</b> place trades. It simulates what would have happened historically.
                </p>

                <h3 className="tsHelp__section">Key Terms</h3>
                <dl className="tsHelp__dl">
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Prediction Horizon</b>:</dt>
                    <dd className="tsHelp__dd">Which prediction period you act on (Daily, Weekly, Monthly, etc.).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Trade Frequency (business days)</b>:</dt>
                    <dd className="tsHelp__dd">Minimum business days between trades (1=every day, 5≈weekly, 21≈monthly).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Predicted Return</b>:</dt>
                    <dd className="tsHelp__dd">The predicted return for the selected horizon (based on predicted vs current price).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Actions</b>:</dt>
                    <dd className="tsHelp__dd">What you do when the predicted return is up/down (Buy, Sell, Short, or No Trade). Sell is flat; Short profits if the stock falls.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Minimum/Maximum Predicted Return</b>:</dt>
                    <dd className="tsHelp__dd">Optional range filter on the predicted return. Example: 0.02 means +2%.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Equity</b>:</dt>
                    <dd className="tsHelp__dd">Simulated portfolio value after each trade.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Cumulative %</b>:</dt>
                    <dd className="tsHelp__dd">Total strategy return over the selected window.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt className="tsHelp__dt"><b>Active Strategy</b>:</dt>
                    <dd className="tsHelp__dd">Your default strategy. We show its recommended action for the latest market day.</dd>
                  </div>
                </dl>

                <h3 className="tsHelp__section">Examples</h3>
                <div className="tsHelp__examples">
                  <div className="tsHelp__exampleCard">
                    <div className="tsHelp__exampleTitle"><b>Daily buy / no-trade</b>:</div>
                    <div className="tsHelp__exampleText">
                      Horizon=Daily, Frequency=1, If Predicted Return ≥ 0=Buy, If Predicted Return &lt; 0=No Trade.
                    </div>
                  </div>
                  <div className="tsHelp__exampleCard">
                    <div className="tsHelp__exampleTitle"><b>Weekly buy / sell</b>:</div>
                    <div className="tsHelp__exampleText">
                      Horizon=Weekly, Frequency=5, If Predicted Return ≥ 0=Buy, If Predicted Return &lt; 0=Sell. This exits or stays flat on negative signals.
                    </div>
                  </div>
                  <div className="tsHelp__exampleCard">
                    <div className="tsHelp__exampleTitle"><b>Weekly long / short</b>:</div>
                    <div className="tsHelp__exampleText">
                      Horizon=Weekly, Frequency=5, If Predicted Return ≥ 0=Buy, If Predicted Return &lt; 0=Short.
                    </div>
                  </div>
                  <div className="tsHelp__exampleCard">
                    <div className="tsHelp__exampleTitle"><b>Only trade when prediction is strong</b>:</div>
                    <div className="tsHelp__exampleText">
                      Set Minimum Predicted Return=0.02 to only trade when the model predicts +2% or higher.
                    </div>
                  </div>
                </div>

                <h3 className="tsHelp__section">Notes</h3>
                <div className="tsHelp__noteBox">
                  <p className="tsHelp__p tsHelp__noteText">All days are business days (not calendar days).</p>
                  <p className="tsHelp__p tsHelp__noteText">Backtests are for educational purposes; past performance does not guarantee future results.</p>
                </div>

                <div className="tsHelp__actions">
                  <button
                    type="button"
                    className="dt-btn orangeBtnByPage dt-action-btn"
                    onClick={() => setShowHelp(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={showTradesHelp}
          className="strategyHelpModal"
          size="lg"
          centered
          onHide={() => setShowTradesHelp(false)}
        >
          <Modal.Body className="tsHelpModalBody">
            <div className="tsHelp">
              <div className="tsHelp__header">
                <div className="tsHelp__headerText">
                  <div className="tsHelp__title">{TRADES_HELP_TITLE}</div>
                </div>

                <button
                  type="button"
                  className="tsHelp__closeIcon"
                  aria-label="Close"
                  onClick={() => setShowTradesHelp(false)}
                >
                  ×
                </button>
              </div>

              <div className="tsHelp__content">
                <p className="tsHelp__p tsHelp__intro">
                  These columns explain each simulated trade decision. Entry Date is when the signal was available (prediction release). Exit Date is the target date for the selected prediction horizon.
                </p>

                <h3 className="tsHelp__section">Columns</h3>
                <dl className="tsHelp__dl">
                  <div className="tsHelp__dlRow">
                    <dt>Row</dt>
                    <dd>Trade number in this backtest run.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Entry Date</dt>
                    <dd>The date the prediction was released (the day you would place the decision).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Exit Date</dt>
                    <dd>The prediction target date for the selected horizon (the date the return is measured).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Predicted %</dt>
                    <dd>The model’s predicted return for the selected horizon (percent units).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Action</dt>
                    <dd>What your strategy chose: Buy, Sell, Short, or No Trade. Sell is flat; Short uses inverse stock exposure.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Realized %</dt>
                    <dd>The actual realized return over that horizon window (percent units).</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Trade %</dt>
                    <dd>The return applied to your portfolio for that row based on Action.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Equity</dt>
                    <dd>Your simulated portfolio value after applying Trade % to your capital.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Cumulative %</dt>
                    <dd>Total compounded return since the start of the backtest.</dd>
                  </div>
                  <div className="tsHelp__dlRow">
                    <dt>Predicted Price</dt>
                    <dd>The model’s predicted price at the Exit Date for the selected horizon.</dd>
                  </div>
                </dl>

                <div className="tsHelp__actions">
                  <button
                    type="button"
                    className="dt-btn orangeBtnByPage dt-action-btn"
                    onClick={() => setShowTradesHelp(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default Strategies;
